/**
 * Endless mode client: the void line, streamed.
 *
 * One EndlessWorld per seed generates segments ahead of the marble; this
 * module builds their render groups (track pieces + island masses/decor in
 * one group per segment), chains their open centerline curves into a global
 * arc-length line, and disposes groups behind the marble. The render window
 * (BEHIND..AHEAD) sits strictly inside the generator's KEEP_SEGMENTS, so
 * everything visible is co-live in the occupancy — nothing on screen can
 * overlap.
 *
 * Scene dressing: deep-space background + fog, a camera-following starfield.
 * No ground plane — islands are the only land.
 */
import * as THREE from "three";
import { fitShadowsTo, type SceneCtx } from "./scene.ts";
import type { Assets } from "./assets.ts";
import { initSeed, newSeed } from "./seed.ts";
import { instantiatePlacements, type Spinner } from "./track.ts";
import { buildOpenCurve } from "./centerline.ts";
import { createTrail } from "./trail.ts";
import { type Cameras, type CamMode, createCameras } from "./camera.ts";
import { EndlessWorld, KEEP_SEGMENTS } from "../gen/endless/world.ts";
import { TRACK_SCALE } from "../gen/endless/scale.ts";
import { ExitAnim, SpawnAnim } from "./streamAnim.ts";
import {
  FIXED_DT,
  MARBLE_RADIUS,
  MARBLE_SCALE,
  PhysicsEngine,
  type PhysicsModule,
} from "./physics/engine.ts";
import {
  attachFrames,
  hasAltBranch,
  hasBraid,
  type Rail,
  railForSegment,
  railFrameAt,
  type RailStationSource,
  stationForce,
  stationGravityDelta,
  stationRestitution,
} from "./physics/rail.ts";
import {
  autopilotForce,
  type BoostState,
  boostStep,
  braidLaneAlt,
  DRAFT_GAIN,
  draftFactor,
  FULL_TANK,
  type Neighbor,
  type Pilot,
  type PilotDebug,
  pilotFor,
  pilotOffset,
  rubberMod,
  temperHeat,
  temperStep,
  wantJump,
} from "./physics/autopilot.ts";
import { racerIdentity, type Rival, rivalFor } from "./peersCore.ts";
import { rngFamily } from "../gen/rng.ts";
import {
  addMarbleCore,
  applyTunnelVariants,
  disposeMarbleOwnedVisuals,
  fresnelShellMaterial,
  glassShellMaterial,
  marbleEnv,
} from "./marbleGlass.ts";
import type { Placement } from "../shared/types.ts";
import {
  affineApply,
  affineUnapply,
  frameUp,
  quatRotate,
  quatUnrotate,
  type SegmentFrame,
  segmentFrameAt,
  twistParam,
} from "../shared/frame.ts";
import {
  bumperProxies,
  islandProxies,
  segmentProxies,
  worldifyProxies,
} from "../gen/endless/proxies.ts";
import { buildIslandInstanced, type InstancedBuild } from "./islandRender.ts";
import { FanField, fanVolumesOf } from "./fanField.ts";
import { buildChuteMesh, extractChannelSection } from "./twistChute.ts";
import { Atmosphere, drawZone } from "./atmosphere.ts";
// Zone cadence lives with the climate now (M21 — a generation input).
import { ZONE_SEGMENTS } from "../gen/climate.ts";
import { buildVoidDecor, type FloatBuild } from "./voidRender.ts";
import type { DecorCatalog } from "../gen/decor.ts";
import { genVersion } from "../gen/version.ts";
import { drawPipeColor } from "./pipeColor.ts";
import { PeerRing } from "./peers.ts";
import { installPhotoKey } from "./photo.ts";
import { AmbientAudio } from "./audio.ts";
import { milestoneFor, MilestoneToast, PlaceHud, racePlace } from "./hud.ts";
import { installCua } from "./cua.ts";
import { editingControl } from "./runControls.ts";

// Per-tile physics modifiers + planner zones live in physics/rail.ts now
// (M11): ONE rail law shared with the headless survivability tests.

/** Segments built ahead of / kept behind the marble's segment. */
const AHEAD = 3;
const BEHIND = 2;

/** Ice-pale pipe recolor for icy zones (zones must READ). */
const ICE_PIPE: readonly [number, number, number] = [178, 224, 240];
/** Violet-charged pipe recolor for gravity-rotation zones. */
const GRAVITY_PIPE: readonly [number, number, number] = [186, 148, 255];
/** Boost surfaces (M19b): sprint zones and braid boost lanes read warm
 * amber — the lane contrast must READ (zones-must-read law). */
const BOOST_PIPE: readonly [number, number, number] = [255, 178, 64];

/** Peer count above which peers render as one InstancedMesh (per-racer
 * kit meshes + cloned materials would swamp the scene). */
const INSTANCED_AT = 32;

// Marble feel — same constants as the finite loop (marble.ts).
const MARBLE_PIECE = "marble-center-high";
const BASE_SPEED = 2.2;
const MIN_SPEED = 1.2;
const MAX_SPEED = 7;
const GRAVITY = 6;
const DRAG = 0.9;

interface BuiltSegment {
  index: number;
  group: THREE.Group;
  curve: THREE.CatmullRomCurve3;
  startS: number;
  length: number;
  spinners: Spinner[];
  islands: number;
  /** Entry pose, kept for the exit animation's wave direction. */
  entry: { x: number; y: number; z: number; dir: number };
  /** Per-build GPU state (island instance buffers + cloned materials). */
  disposables: InstancedBuild[];
  /** Floating void decor builds (M18b) — ticked in finishFrame once the
   * spawn wave is done; disposed with the segment. */
  floats: FloatBuild[];
  /** Lowest channel base (PLAN-space y, scaled) — fall detection compares
   * in plan space (a reoriented racer "falls" along its local down). */
  lowY: number;
  /** Presentation frame (M16 gravity reorientation) — undefined = identity. */
  frame?: SegmentFrame;
  /** Exit-framed half of a twist segment (placements past the run). */
  postGroup?: THREE.Group;
  /** Authored channel pieces that can swallow the follow camera. */
  cameraOccluders: THREE.Object3D[];
  /** Glass/wire tunnel shells that fade only while containing the lens. */
  cameraFadeMeshes: THREE.Mesh[];
}

interface UiRefs {
  seedEl: HTMLElement | null;
  statsEl: HTMLElement | null;
  newTrackBtn: HTMLButtonElement | null;
  camBtn: HTMLButtonElement | null;
  onError?: (err: unknown) => void;
  onReady?: () => void;
}

export function bootEndless(
  ctx: SceneCtx,
  assets: Assets,
  catalog: DecorCatalog,
  ui: UiRefs,
  physics: PhysicsModule | null = null,
): void {
  // The void: stars pinned to the camera; sky/fog/light/weather belong to
  // the per-seed Atmosphere (a generative family of moods, drawn per zone).
  const stars = makeStarfield();
  ctx.scene.add(stars);
  ctx.onFrame.add(() => {
    stars.position.copy(ctx.camera.position);
  });

  // Slalom-scale pieces would swallow the default follow cam — ride
  // higher and farther back; island masses are occluders (the follow cam
  // slides over shoulders instead of clipping through them).
  const cams = createCameras(ctx, null, null, {
    trail: 7,
    height: 3.1,
    occluders: () => session?.cameraOccluders() ?? [],
    // Gravity reorientation (M16): the lens rolls with the subject's
    // local gravity frame (eased inside camera.ts).
    up: () => session?.cameraUp() ?? [0, 1, 0],
  });
  const setCamLabel = (mode: CamMode) => {
    if (ui.camBtn) {
      ui.camBtn.textContent = mode === "orbit" ? "Follow cam" : "Orbit cam";
      ui.camBtn.setAttribute("aria-pressed", String(mode === "follow"));
    }
  };
  cams.onModeChange = setCamLabel;

  let session: Session | null = null;
  const leaderBtn = document.getElementById("leader-cam");
  const toggleLeader = () => {
    if (!session?.canFollowLeader) return;
    session?.toggleLeaderCam();
    const active = session?.raceStats()?.leaderCam ?? false;
    if (leaderBtn) {
      leaderBtn.textContent = active ? "Race leader" : "Your marble";
      leaderBtn.setAttribute("aria-pressed", String(active));
    }
  };
  leaderBtn?.addEventListener("click", toggleLeader);

  // Ambient audio (M13): procedural mood pad + rolling voice + event
  // cues. Muted until the user's toggle gesture (autoplay policy); the
  // M key or the HUD button.
  const audio = new AmbientAudio();
  const audioBtn = document.getElementById(
    "audio-toggle",
  ) as HTMLButtonElement | null;
  const setAudioLabel = () => {
    if (audioBtn) {
      audioBtn.textContent = audio.enabled ? "audio: on" : "audio: off";
    }
  };
  const toggleAudio = () => {
    audio.toggle();
    if (audio.enabled && session) audio.setMood(session.atmosphere.name);
    setAudioLabel();
  };
  if (audioBtn) {
    audioBtn.addEventListener("click", toggleAudio);
  }
  addEventListener("keydown", (e) => {
    if (
      editingControl(e.target) || e.repeat || e.metaKey || e.ctrlKey || e.altKey
    ) return;
    if (e.key === "m" || e.key === "M") toggleAudio();
    // SPACE elects leader-cam (M15): the camera chases the race leader
    // instead of the hero marble. Hero-follow is the default — a camera
    // that silently switches subjects mid-run reads as confusing.
    if (e.code === "Space" && session?.canFollowLeader) {
      if ((e.target as HTMLElement | null)?.closest("button, summary")) return;
      e.preventDefault();
      toggleLeader();
    }
  });

  // P = live photo export (user rule: never freeze the run).
  const photo = installPhotoKey(
    ctx,
    () =>
      `marbles-${session?.seed ?? "void"}-${
        Math.round(session?.marbleS ?? 0)
      }u`,
  );

  ctx.onFrame.add((dt) => {
    session?.update(dt);
    cams.update(dt);
    session?.fadeCameraTunnels(dt);
  });

  initSeed((seed) => {
    session?.dispose();
    session = null;
    try {
      session = new Session(
        seed,
        ctx,
        assets,
        catalog,
        cams,
        ui,
        stars,
        physics,
        audio,
      );
      if (audio.enabled) audio.setMood(session.atmosphere.name);
    } catch (err) {
      console.error(err);
      if (ui.statsEl) {
        ui.statsEl.textContent = `error: ${
          err instanceof Error ? err.message : String(err)
        }`;
      }
      ui.onError?.(err);
      return;
    }
    // Endless is a journey — follow by default.
    cams.setMode("follow");
    setCamLabel(cams.mode);
    if (leaderBtn) {
      leaderBtn.hidden = !session.canFollowLeader;
      leaderBtn.textContent = "Your marble";
      leaderBtn.setAttribute("aria-pressed", "false");
    }
    for (
      const hint of document.querySelectorAll<HTMLElement>(".endless-help")
    ) {
      hint.hidden = !session.canFollowLeader;
    }
    ui.onReady?.();
  });

  ui.newTrackBtn?.addEventListener("click", () => newSeed());
  ui.camBtn?.addEventListener("click", () => cams.toggle());
  ui.seedEl?.addEventListener("click", () => {
    navigator.clipboard?.writeText(location.href).catch(() => {});
  });

  // Debug handle (dev tooling, same spirit as __ctx): harness probes
  // reach the live session for camera/sampling forensics.
  Object.defineProperty(globalThis, "__session", { get: () => session });
  installCua(ctx, () => ({
    mode: "endless",
    seed: session?.seed ?? "",
    version: genVersion(),
    camMode: cams.mode,
    marble: session
      ? {
        s: Number(session.marbleS.toFixed(2)),
        speed: Number(session.speed.toFixed(2)),
        pos: session.marbleObj.position.toArray().map((v: number) =>
          Number(v.toFixed(2))
        ),
        segment: session.currentIndex,
      }
      : null,
    // The race (M15): every real racer's progress + the current leader.
    race: session ? session.raceStats() : null,
    stream: session
      ? {
        built: session.builtRange(),
        generated: session.world.generatedCount,
        islandsSeen: session.islandsSeen,
        occupancyCells: session.world.occupancySize,
        theme: session.world.style.theme,
        // Leak gate: total scene nodes must stay bounded over any drive —
        // an exit wave that never finishes would show up here.
        sceneNodes: countNodes(ctx.scene),
        // Animation lifecycle, for rule-based verification.
        spawningSegments: session.spawningCount,
        exitingSegments: session.exitingCount,
        atmosphere: session.atmosphere.name,
        weather: session.atmosphere.weatherKind,
        physics: session.physicsActive,
        railDebug: session.railDebugState,
        railDebugSummary: session.railDebugSummary,
        deaths: session.deaths,
        // Peer ring lifecycle (marble ring): live count + relative arcs.
        peers: session.peerRing.liveCount,
        peerRing: session.peerRing.stats(session.marbleS),
        milestone: session.toast.last,
        place: session.placeHud.last,
        photos: photo.count,
        audio: audio.enabled,
        // Instancing gate: island pieces share draw calls now.
        drawCalls: ctx.renderer.info.render.calls,
      }
      : null,
  }));
}

/** Place-readout refresh period (seconds) — 4Hz; the count is O(N). */
const PLACE_REFRESH = 0.25;

/** Rubber-banding EMA time constant (seconds) — rank order flaps in a
 * tight pack; skill modulation follows the smoothed position. */
const RUBBER_TAU = 2;

// Overtake commitment (M20a). OVERTAKE_OFF stays strictly below the
// autopilot's CORRIDOR_DEAD (0.3) so the corridor spring never fights
// the held line and the channel wall never enters play.
const OVERTAKE_OFF = 0.27;
const OTK_ENGAGE = 3.5;
const OTK_DISENGAGE = 5;
const OTK_SLEW_TAU = 0.4;
/** Rider draft cap (M20a): riders chained nose-to-tail must never
 * compound into a glide that outruns the hero-anchored stream. */
const RIDER_DRAFT_CAP = 1.08;

// Rivalry expression (M20b).
/** Contact-proxy distance for the temper trigger (~2.5 marble radii). */
const CONTACT_NEAR = 0.25;
/** Defensive-block trigger range (world units, scaled by aggro and
 * grudge intensity — mirrors BOOST_DEFEND's shape). */
const BLOCK_RANGE = 4;
/** Blocking line offset — inside CORRIDOR_DEAD like the overtake. */
const BLOCK_OFF = 0.25;
/** Braid lane mirroring engages when the rival is this close (world u). */
const RIVAL_MIRROR_S = 10;
/** A marble-static knock this fast leaves a mark (M25 world memory). */
const SCAR_SPEED = 4.5;

// --- physics LOD (pack-scale realtime) -------------------------------------
//
// N=9999 ran at ~0.27x realtime: the 10k-body Box3D step was the wall.
// Racers far from the camera subject demote to KINEMATIC RIDERS — no
// body, they glide along the rail at a personality-flavored cruise —
// and promote back to real bodies as the subject nears (spawned on the
// rail with a short ghost window so re-entry never detonates the pack).
// The hero and the camera subject are always real.

/** LOD only engages above this pack size (small packs stay fully real). */
const LOD_ENGAGE_N = 300;
/** Max real bodies (the nearest-by-progress band around the subject). */
const LIVE_CAP = 240;
/** Progress window around the subject (world units): promote inside
 * PROMOTE, demote outside DEMOTE — the gap is the hysteresis. */
const LOD_PROMOTE_S = 42;
const LOD_DEMOTE_S = 55;
/** Re-evaluate the live set every this many fixed steps. */
const LOD_PERIOD = 30;

/** Respawn spawn-in envelope (seconds): the visual eases ~0.1 → 1 instead
 * of popping (the initial grid avalanche keeps its pop — it reads fine). */
const SPAWN_IN_T = 0.4;
/** Respawn collision immunity (seconds): a fresh marble ghosts through
 * neighbors (track collisions stay real) until it is clear of the pack. */
const GHOST_T = 2;
/** Ghost hard cap (seconds) — a still-overlapped marble resolidifies here
 * regardless, so nobody ghosts forever. */
const GHOST_MAX = 4;
/** Resolidify clearance: no other racer within ~2.5 marble radii — expiry
 * while overlapped fires the violent depenetration pop the ghost exists
 * to avoid. */
const GHOST_CLEAR_D = MARBLE_RADIUS * 2.5;

/** One physics racer (M15): the hero (idx 0) or a real peer marble. */
interface Racer {
  idx: number;
  pilot: Pilot;
  /** Effective pilot this decision (rubber-banding writes skill-modulated
   * bravery/eagerness here each decide; `pilot` is the base identity). */
  pilotEff: Pilot;
  /** Physics LOD: "live" = real Box3D body; "rider" = kinematic glide
   * along the rail (far from the camera subject at pack scale). */
  lod: "live" | "rider";
  /** Smoothed race-position fraction (0 = leading, 1 = last) — rank order
   * flaps in a tight pack, so the modulation follows a ~2s EMA. */
  rubber: number;
  mass: number;
  /** Scene objects — null for instanced peers (big packs render through
   * one InstancedMesh instead of per-racer groups). */
  obj: THREE.Object3D | null;
  pivot: THREE.Object3D | null;
  materials: THREE.Material[];
  nearest: { seg: number; idx: number };
  /** Braid lane (M17): when `laneBraid` names the racer's CURRENT braid
   * segment and `laneAlt` is set, its rail is that segment's alt rail —
   * the pack visibly splits and rejoins. Choice is per-racer determinism
   * (braidLaneAlt — each racer's own label-derived stream); collisions
   * that physically shove a marble onto the other lane flip it there
   * (decide()'s recovery law). */
  laneAlt: boolean;
  laneBraid: number;
  s: number;
  speed: number;
  simT: number;
  lastProgressS: number;
  stallClock: number;
  stallHopped: boolean;
  wasHop: boolean;
  /** Respawn spawn-in clock (seconds left; 0 = done) — physicsMove eases
   * the visual scale while it runs. */
  spawnAnim: number;
  /** Respawn ghost clock (seconds until eligible to resolidify; 0 =
   * solid). Runs negative toward the hard cap while still overlapped. */
  ghostT: number;
  /** Fuel-boost economy (competition round). */
  boost: BoostState;
  wasBoosting: boolean;
  /** Last force step's introspection — headroom feeds the boost law. */
  dbg: PilotDebug;
  /** Persistent state scratch (filled from the batched readback each
   * step — no per-step allocation at pack scale). */
  pos: [number, number, number];
  vel: [number, number, number];
  quat: [number, number, number, number];
  /** Cached control force between decision steps (large packs decimate
   * the DECISION rate round-robin; the force still applies every step). */
  cachedF: [number, number, number] | null;
  /** Ember phenotype baseline — boost-glow flameout restores it. */
  baseEmissive: THREE.Color | null;
  /** Draft multiplier this decision (M20a) — 1 outside a wake. Reset on
   * LOD transitions and respawns (state-clean replays). */
  draft: number;
  /** Overtake-commitment blend 0..1 (M20a): eases the offset between the
   * wander line and the chirality passing line (slew kills offset
   * flapping against separation steering). */
  otkBlend: number;
  /** Progress rank this step (index into the s-sorted pack) — written in
   * the sort pass; riders read their leader through it. */
  rank: number;
  /** Last ice-scuff sim time (M25 world memory rate limit). */
  lastScuffT: number;
  /** Seeded directed relationship (M20b) — resolved once at pack build. */
  rival: Rival | null;
  /** Post-contact temper window remaining (seconds; 0 = cool). */
  temperClock: number;
  /** Defensive-block blend 0..1 (M20b) — same slew law as otkBlend;
   * overtake > block > wander on the single offset actuator. */
  blockBlend: number;
}

type RailDebugReason = "lod-distance" | "fall" | "stall" | "stream-cull";

interface RailDebugTile {
  segment: number;
  station: number;
  window: number | null;
  placementIndex: number | null;
  pieceId: string | null;
  family: string | null;
  zone: string | null;
  alt: boolean;
  part: number | null;
}

interface RailDebugEvent {
  kind: "rail-handoff" | "recovery";
  reason: RailDebugReason;
  tileCandidate: boolean;
  count: number;
  seed: string;
  racer: number;
  s: number;
  from: RailDebugTile;
  to?: RailDebugTile;
}

interface RailDebugPieceStat {
  pieceId: string;
  family: string | null;
  events: number;
  falls: number;
  stalls: number;
  generatedWindows: number;
}

interface RailDebugSummary {
  candidateEvents: number;
  generatedWindows: number;
  byPiece: RailDebugPieceStat[];
}

/** Reusable neighbor slots (separation steering) — mutated in place. */
interface NeighborSlot {
  pos: [number, number, number];
  bravery: number;
}

class Session {
  readonly world: EndlessWorld;
  readonly seed: string;
  marbleS = 0;
  speed = BASE_SPEED;
  islandsSeen = 0;
  currentIndex = 0;
  readonly marbleObj: THREE.Group;

  private readonly root = new THREE.Group();
  private readonly cameraLocal = new THREE.Vector3();
  private readonly built = new Map<number, BuiltSegment>();
  /** Segments assembling in (index → wave) and sinking out. */
  private readonly spawning = new Map<number, SpawnAnim>();
  private exiting: {
    group: THREE.Group;
    anim: ExitAnim;
    disposables: InstancedBuild[];
  }[] = [];
  /** The session's first fill builds instantly — the opening view should
   * exist, not assemble around the spawning marble. */
  private initialBuild = true;
  /** Cumulative curve arc lengths, kept for every index ever built. */
  private readonly sMeta = new Map<number, { start: number; len: number }>();
  private readonly pivot: THREE.Group;
  private radius = 0.1;
  private statsClock = 0;

  readonly atmosphere: Atmosphere;
  /** The revolving ring of seeded NPC marbles around the player. */
  readonly peerRing: PeerRing;
  /** Distance milestone toast (thresholds pure in hud.ts). */
  readonly toast = new MilestoneToast();
  /** Minimal leaderboard: `#place/N` (counting pure in hud.ts). */
  readonly placeHud = new PlaceHud();
  /** Place refresh throttle — the O(N) count runs at 4Hz, not 60. */
  private placeClock = PLACE_REFRESH;
  /** Box3D driver (null → kinematic rail rider, ?phys=0). */
  private engine: PhysicsEngine | null = null;
  /** The per-step control law: the autopilot (M14a) unless ?autopilot=0
   * falls back to the old assist choreographer for A/B comparison. */
  private readonly useAutopilot =
    new URLSearchParams(location.search).get("autopilot") !== "0";
  /** The racers (M15): index 0 is the hero (existing marble visuals +
   * checkpoint escalation); the rest are real peer marbles with their own
   * pilots, respawning behind the pack tail. Empty in kinematic mode. */
  private racers: Racer[] = [];
  /** Real racer-peer count (0 outside physics-autopilot mode). */
  private realPeerCount = 0;
  /** Decorative-ring size when real racers are off. */
  private ringPeerCount: number | undefined = undefined;
  /** Leader-cam (M15): OFF by default — the camera follows the hero (who
   * carries the trail); SPACE elects chasing the race leader instead. */
  private leaderCam = false;
  /** The pivot the trail rides — whichever marble the camera follows. */
  private followedPivot: THREE.Object3D;
  /** Racer idx the camera currently follows (leader-cam hysteresis). */
  private followedIdx = 0;
  /** Progress-sorted racer order (rebuilt per fixed step, reused). */
  private readonly sorted: Racer[] = [];
  /** Separation-partner scratch (reused per decision). */
  private readonly othersScratch: Neighbor[] = [];
  private stepCounter = 0;
  /** Instanced peer rendering (packs > INSTANCED_AT): opaque core mesh
   * plus an additive fresnel glass shell mesh. */
  private peerMesh: THREE.InstancedMesh | null = null;
  private peerShellMesh: THREE.InstancedMesh | null = null;
  private readonly peerColors: THREE.Color[] = [];
  /** Camera/trail stand-in when the followed racer is instanced. */
  private readonly instProxy = new THREE.Object3D();
  private readonly instMatrix = new THREE.Matrix4();
  private readonly instPos = new THREE.Vector3();
  private readonly instQuat = new THREE.Quaternion();
  private readonly instScale = new THREE.Vector3(1, 1, 1);
  private readonly rails = new Map<number, Rail>();
  /** ?debug=1 provenance for every physics→rail/recovery handoff. Falls
   * and stalls are tile candidates; distance LOD and stream culling are
   * called out separately so optimization does not masquerade as bad flow. */
  private readonly railDebug =
    new URLSearchParams(location.search).get("debug") === "1";
  private readonly railDebugCounts = new Map<string, number>();
  private readonly railDebugRecent: RailDebugEvent[] = [];
  private railDebugCandidateEvents = 0;
  private readonly railDebugPieces = new Map<string, RailDebugPieceStat>();
  private railDebugGeneratedWindows = 0;
  /** Braid alt-lane rails (M17): built alongside `rails` for every
   * segment carrying an alt branch; per-racer lane choice picks between
   * them on braid segments (railFor). */
  private readonly altRails = new Map<number, Rail>();
  /** Built segment indices hosting a braid — the only segments where
   * per-racer lane choice engages (diamonds/spurs keep branchOf). */
  private readonly braidSegs = new Set<number>();
  /** Fan wind volumes, binned per segment (M16) — fans push marbles. */
  private readonly fanField = new FanField();
  /** V3: continuation segment → its fork segment (lane-label carrier). */
  private readonly braidContOf = new Map<number, number>();
  /** Accumulated sim time for pulsed wind (M19a gust walls). */
  private windT = 0;
  /** Respawn timestamps inside the trailing 2s (M20a pile-up telemetry). */
  private readonly respawnTimes: number[] = [];
  /** Racers hit (marble-marble, above threshold) since their last decide
   * — the temper trigger's REAL contact source (contact-events round). */
  private readonly hitFlags = new Set<number>();
  /** Hard static knocks awaiting world-memory marks (M25). */
  private readonly pendingScars: {
    x: number;
    y: number;
    z: number;
    speed: number;
    idx: number;
  }[] = [];
  /** M25 world memory: shared scar geometry/material (never disposed per
   * mark) + per-segment mark counts + furniture mid-tip animations. */
  private scarGeo: THREE.SphereGeometry | null = null;
  private scarMat: THREE.MeshStandardMaterial | null = null;
  private scuffMat: THREE.MeshStandardMaterial | null = null;
  private readonly scarCount = new Map<number, number>();
  private readonly tipping: {
    obj: THREE.Object3D;
    axis: THREE.Vector3;
    target: number;
    t: number;
  }[] = [];
  /** Grudge-gated boost ignitions (M20b declustering telemetry). */
  private rivalIgnitions = 0;
  /** Rival HUD token flag (M20b, ?rival=1) — default off until review. */
  private readonly rivalHud =
    new URLSearchParams(location.search).get("rival") === "1";
  /** Scratch for the per-decision wind force (allocation-free). */
  private readonly windScratch: [number, number, number] = [0, 0, 0];
  /** M16 conjugation scratch (reoriented segments): marble state maps
   * into plan space for the control laws, forces map back out. */
  private readonly planPos: [number, number, number] = [0, 0, 0];
  private readonly planVel: [number, number, number] = [0, 0, 0];
  private readonly planF: [number, number, number] = [0, 0, 0];
  private readonly frameScratch: [number, number, number] = [0, 0, 0];
  /** Pooled neighbor slots (separation partners conjugate per-racer). */
  private readonly neighborPool: {
    pos: [number, number, number];
    bravery: number;
  }[] = Array.from({ length: 4 }, () => ({
    pos: [0, 0, 0] as [number, number, number],
    bravery: 1,
  }));
  /** Local up of the camera subject (follow-cam rolls with the frame);
   * target + eased value — one-frame frame flips must never slam it. */
  private readonly camUp: [number, number, number] = [0, 1, 0];
  private readonly camUpTarget: [number, number, number] = [0, 1, 0];
  /** Active diamond branch per segment — respawns alternate it, so a
   * fall's retry may ride the OTHER branch (respawn variety). */
  private readonly branchOf = new Map<number, "main" | "alt">();
  /** Pipe color per island chapter (drawn once, cached). */
  private readonly chapterColors = new Map<
    number,
    readonly [number, number, number] | null
  >();

  private chapterColor(
    chapter: number,
  ): readonly [number, number, number] | null {
    let c = this.chapterColors.get(chapter);
    if (c === undefined) {
      c = drawPipeColor(this.seed, chapter);
      this.chapterColors.set(chapter, c);
    }
    return c;
  }
  private nearest: { seg: number; idx: number } = { seg: 0, idx: 0 };
  deaths = 0;
  /** Stall net: no rail progress for a while → respawn. Progress must
   * move forward NO MATTER WHAT (user rule) — falls and wedges alike. */
  private stallClock = 0;
  private lastProgressS = 0;
  /** Escalation: repeated deaths at one checkpoint skip forward past the
   * problem geometry (each such spot is a future named scenario). */
  private respawnsHere = 0;
  private lastRespawnSeg = -1;
  private zone = 0;
  /** Audio cue edge detectors (launch/land/boost transitions). */
  private wasFlight = false;
  private wasSurge = false;
  /** One escape hop per stall episode (M14b): a parked marble tries a
   * jump before the respawn net takes it. */
  private stallHopped = false;
  private focus: { center: THREE.Vector3; radius: number } | null = null;

  private readonly trail = createTrail();
  private readonly boostTrail = createTrail(0xff5a24, {
    maxPoints: 28,
    headAlpha: 1,
    additive: true,
    renderOrder: 6,
  });
  private boostTrailFade = 0;
  private boostTrailActive = false;
  private readonly trailPoint = new THREE.Vector3();
  private readonly point = new THREE.Vector3();
  private readonly tangent = new THREE.Vector3();
  private readonly axis = new THREE.Vector3();
  private readonly spin = new THREE.Quaternion();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(
    seed: string,
    private readonly ctx: SceneCtx,
    private readonly assets: Assets,
    catalog: DecorCatalog,
    private readonly cams: Cameras,
    private readonly ui: UiRefs,
    stars: THREE.Points,
    physics: PhysicsModule | null = null,
    private readonly audio: AmbientAudio = new AmbientAudio(),
  ) {
    this.seed = seed;
    if (physics) this.engine = new PhysicsEngine(physics);
    this.world = new EndlessWorld(seed, catalog);
    // Base palette stays stock — pipe color is PER ISLAND CHAPTER (user
    // rule), applied per placement at build time.
    assets.applyPalette(this.world.style.paletteIndex);
    this.root.name = `endless:${seed}`;
    ctx.scene.add(this.root);
    this.atmosphere = new Atmosphere(
      ctx,
      stars,
      drawZone(seed, 0, this.zoneBiome(0)),
    );

    // Session peer sizing first — the hero's shell material depends on it
    // (transmission glass only below pack scale; see cloneOne).
    {
      const peersParam = new URLSearchParams(location.search).get("peers");
      const peerCount = peersParam === null
        ? undefined
        : Math.max(0, Math.min(9998, Number.parseInt(peersParam, 10) || 0));
      const realRace = this.engine !== null && this.useAutopilot;
      this.realPeerCount = realRace ? Math.min(9998, peerCount ?? 5) : 0;
      this.ringPeerCount = realRace ? 0 : peerCount;
    }

    // Marble (same grounded-mesh-in-pivot construction as the loop mode).
    this.marbleObj = new THREE.Group();
    this.marbleObj.name = "marble";
    this.pivot = new THREE.Group();
    this.marbleObj.add(this.pivot);
    const mesh = assets.getPiece(MARBLE_PIECE);
    const heroMaterials: THREE.Material[] = [];
    if (mesh) {
      // Half the kit's authored size (user: "shrink the ball 50%") —
      // matches the physics MARBLE_RADIUS.
      mesh.scale.setScalar(MARBLE_SCALE);
      // The kit shell is empty glass — inject the opaque core the glass
      // refracts (hero: soft white) so the marble reads SOLID, not as a
      // washed-out bubble (user report). Added before the material pass
      // so boost glow reaches it.
      addMarbleCore(mesh);
      // Own materials (boost glow writes emissive; shared kit materials
      // would light every piece using them).
      mesh.traverse((child: THREE.Object3D) => {
        const m = child as THREE.Mesh;
        if (!m.isMesh) return;
        const cloneOne = (mat: THREE.Material): THREE.Material => {
          // Hero shell: clear real glass (neutral phenotype); stable
          // transparent order (see buildRacerVisual). At PACK scale the
          // transmission buffer (opaques only) erases the hundreds of
          // transparent-shelled peers behind the hero — glitch city — so
          // the hero drops to plain alpha glass there.
          if (mat.name === "glass" && physics) {
            m.renderOrder = 10;
            if (this.realPeerCount > INSTANCED_AT) {
              const g = new THREE.MeshPhysicalMaterial({
                color: 0xe8f2fc,
                transparent: true,
                opacity: 0.3,
                roughness: 0.05,
                metalness: 0,
                // Same one-layer, half-strength reflection law as the
                // transmission shell (see glassShellMaterial) — a hot env plus
                // a stacked clearcoat washed the marble to a pale blue.
                envMap: marbleEnv(ctx.renderer),
                envMapIntensity: 0.5,
                depthWrite: false,
              });
              heroMaterials.push(g);
              return g;
            }
            const g = glassShellMaterial(ctx.renderer, {
              meshId: MARBLE_PIECE,
              tint: null,
              shell: [1, 1, 1],
              effect: { kind: "none", intensity: 0 },
            });
            heroMaterials.push(g);
            return g;
          }
          const clone = mat.clone();
          heroMaterials.push(clone);
          return clone;
        };
        m.material = Array.isArray(m.material)
          ? m.material.map(cloneOne)
          : cloneOne(m.material);
      });
      const box = new THREE.Box3().setFromObject(mesh);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      this.radius = Math.max(size.x, size.y, size.z) / 2 || 0.1;
      this.pivot.position.copy(center);
      mesh.position.sub(center);
      this.pivot.add(mesh);
    }
    this.trail.setWidth(this.radius * 0.6);
    this.boostTrail.setWidth(this.radius * 1.5);
    this.boostTrail.obj.name = "boost-trail";
    this.followedPivot = this.pivot;
    ctx.scene.add(this.marbleObj);
    ctx.scene.add(this.trail.obj);
    ctx.scene.add(this.boostTrail.obj);
    // Decorative ring only outside physics-autopilot mode (real racers
    // replace it there) — sizing computed above, before the hero mesh.
    this.peerRing = new PeerRing(seed, assets, ctx.scene, this.ringPeerCount);

    this.ensure();
    this.initialBuild = false;
    const first = this.built.get(0)!;
    first.curve.getPointAt(0, this.point);
    this.marbleObj.position.copy(this.point);
    if (this.engine) {
      const rail = this.rails.get(0)!;
      const t = rail.tans[0];
      this.engine.spawnMarble(
        0,
        [rail.pts[0][0], rail.pts[0][1] + 0.4, rail.pts[0][2]],
        [t[0] * 2.2, 0, t[2] * 2.2],
      );
      this.racers.push({
        idx: 0,
        pilot: pilotFor(seed, 0),
        pilotEff: pilotFor(seed, 0),
        lod: "live",
        rubber: 0.5,
        mass: this.engine.massOf(0),
        obj: this.marbleObj,
        pivot: this.pivot,
        materials: heroMaterials,
        nearest: { seg: 0, idx: 0 },
        laneAlt: false,
        laneBraid: -1,
        s: 0,
        speed: 0,
        simT: 0,
        lastProgressS: 0,
        stallClock: 0,
        stallHopped: false,
        wasHop: false,
        spawnAnim: 0,
        ghostT: 0,
        boost: { ...FULL_TANK },
        wasBoosting: false,
        dbg: {},
        pos: [0, 0, 0],
        vel: [0, 0, 0],
        quat: [0, 0, 0, 1],
        cachedF: null,
        baseEmissive: null,
        draft: 1,
        otkBlend: 0,
        rank: 0,
        rival: null,
        temperClock: 0,
        blockBlend: 0,
        lastScuffT: 0,
      });
      // Racer peers (M15): real bodies on a wrapped grid start —
      // stations × 3 lanes, then stacked tiers above (a huge pack rains
      // in as a marble avalanche). Kit meshes up to INSTANCED_AT peers;
      // one InstancedMesh beyond.
      const instanced = this.realPeerCount > INSTANCED_AT;
      if (instanced) {
        // Opaque colored core inside an additive fresnel glass rim (the
        // instanced glass illusion — order-independent, sorts never).
        const geo = new THREE.SphereGeometry(MARBLE_RADIUS * 0.82, 12, 8);
        const mat = new THREE.MeshStandardMaterial({
          roughness: 0.35,
          metalness: 0.05,
        });
        this.peerMesh = new THREE.InstancedMesh(geo, mat, this.realPeerCount);
        this.peerMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.peerMesh.frustumCulled = false;
        ctx.scene.add(this.peerMesh);
        const shellGeo = new THREE.SphereGeometry(MARBLE_RADIUS, 12, 8);
        this.peerShellMesh = new THREE.InstancedMesh(
          shellGeo,
          fresnelShellMaterial(),
          this.realPeerCount,
        );
        this.peerShellMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.peerShellMesh.frustumCulled = false;
        ctx.scene.add(this.peerShellMesh);
      }
      const span = Math.max(2, Math.min(rail.pts.length - 2, 120));
      const LANES = [-0.26, 0, 0.26];
      // Grid slots for hero + peers: the hero draws a MID-PACK slot from
      // the designed distribution (uniform over the 20th–80th percentile,
      // label rng — user rule: not first, not last, seed-varied); peers
      // fill the remaining slots in order.
      const totalSlots = this.realPeerCount + 1;
      const heroSlot = Math.min(
        totalSlots - 1,
        Math.floor(
          (0.2 + rngFamily(seed, "grid/hero").float() * 0.6) * totalSlots,
        ),
      );
      const slotPos = (
        k: number,
      ): { at: number; lane: number; tier: number } => ({
        at: 1 + (k % span),
        lane: LANES[k % LANES.length],
        tier: Math.floor(k / (span * LANES.length)),
      });
      {
        const hs = slotPos(heroSlot);
        const tt = rail.tans[hs.at];
        const nl = Math.hypot(tt[2], tt[0]) || 1;
        this.engine.spawnMarble(0, [
          rail.pts[hs.at][0] + (tt[2] / nl) * hs.lane,
          rail.pts[hs.at][1] + 0.4 + hs.tier * 0.35,
          rail.pts[hs.at][2] + (-tt[0] / nl) * hs.lane,
        ], [tt[0] * 2.2, 0, tt[2] * 2.2]);
        const hero = this.racers[0];
        hero.nearest = { seg: 0, idx: hs.at };
        hero.s = rail.s[hs.at];
        hero.lastProgressS = hero.s;
        this.marbleS = hero.s;
      }
      for (let i = 1; i <= this.realPeerCount; i++) {
        const k = i - 1 < heroSlot ? i - 1 : i;
        const { at, lane, tier } = slotPos(k);
        const tt = rail.tans[at];
        const nl = Math.hypot(tt[2], tt[0]) || 1;
        const gridPos: [number, number, number] = [
          rail.pts[at][0] + (tt[2] / nl) * lane,
          rail.pts[at][1] + 0.4 + tier * 0.35,
          rail.pts[at][2] + (-tt[0] / nl) * lane,
        ];
        const gridVel: [number, number, number] = [
          tt[0] * 2.2,
          0,
          tt[2] * 2.2,
        ];
        const gf = railFrameAt(rail, at);
        if (gf) {
          affineApply(gf, gridPos, gridPos);
          quatRotate(gf.q, gridVel, gridVel);
        }
        this.engine.spawnMarble(i, gridPos, gridVel);
        let obj: THREE.Group | null = null;
        let pivot: THREE.Group | null = null;
        let materials: THREE.Material[] = [];
        let baseEmissive: THREE.Color | null = null;
        if (!instanced) {
          ({ obj, pivot, materials, baseEmissive } = this.buildRacerVisual(i));
        } else {
          // Instanced path: the phenotype folds into the instance color
          // (one material — shell multiplies, effects approximate).
          const identity = racerIdentity(seed, i);
          const color = identity.tint
            ? new THREE.Color(
              identity.tint[0] / 255,
              identity.tint[1] / 255,
              identity.tint[2] / 255,
            )
            : new THREE.Color(0xdfe8f2);
          color.multiply(
            new THREE.Color(
              identity.shell[0],
              identity.shell[1],
              identity.shell[2],
            ),
          );
          const ei = identity.effect.intensity;
          if (identity.effect.kind === "ember") {
            color.multiplyScalar(1 + 0.5 * ei);
          } else if (identity.effect.kind === "pearl") {
            color.lerp(new THREE.Color(1, 1, 1), 0.3 * ei);
          } else if (identity.effect.kind === "ghost") {
            color.multiplyScalar(0.55);
          }
          this.peerColors.push(color);
          this.peerMesh!.setColorAt(i - 1, color);
          // Shell rim tint: the phenotype shell (near-white = clear).
          this.peerShellMesh!.setColorAt(
            i - 1,
            new THREE.Color(
              identity.shell[0],
              identity.shell[1],
              identity.shell[2],
            ),
          );
        }
        this.racers.push({
          idx: i,
          pilot: pilotFor(seed, i),
          pilotEff: pilotFor(seed, i),
          lod: "live",
          rubber: 0.5,
          mass: this.engine.massOf(i),
          obj,
          pivot,
          materials,
          nearest: { seg: 0, idx: at },
          laneAlt: false,
          laneBraid: -1,
          s: rail.s[at],
          speed: 0,
          simT: 0,
          lastProgressS: rail.s[at],
          stallClock: 0,
          stallHopped: false,
          wasHop: false,
          spawnAnim: 0,
          ghostT: 0,
          boost: { ...FULL_TANK },
          wasBoosting: false,
          dbg: {},
          pos: [0, 0, 0],
          vel: [0, 0, 0],
          quat: [0, 0, 0, 1],
          cachedF: null,
          baseEmissive,
          draft: 1,
          otkBlend: 0,
          rank: 0,
          rival: null,
          temperClock: 0,
          blockBlend: 0,
          lastScuffT: 0,
        });
      }
      if (this.peerMesh?.instanceColor) {
        this.peerMesh.instanceColor.needsUpdate = true;
      }
      if (this.peerShellMesh?.instanceColor) {
        this.peerShellMesh.instanceColor.needsUpdate = true;
      }
    }

    // Rivalries (M20b): resolved once the pack size is final. Directed,
    // label-pure per racer — n enters only at the target mod.
    for (const r of this.racers) {
      r.rival = rivalFor(seed, r.idx, this.racers.length);
    }

    this.cameraProxy = {
      obj: this.marbleObj,
      s: this.marbleS,
      speed: this.speed,
      radius: this.radius,
      update: () => {},
    };
    this.cams.retargetPath(
      (s, out) => this.samplePoint(s, out),
      this.cameraProxy,
    );
    if (this.ui.seedEl) this.ui.seedEl.textContent = seed;
    this.refreshStats();
  }

  /** Race telemetry for CUA/debug: per-racer progress + the leader. */
  raceStats(): {
    n: number;
    leader: number;
    leaderCam: boolean;
    respawnBurst: number;
    spreadP10P90: number;
    rival: { target: number; valence: string } | null;
    rivalIgnitions: number;
    maxTrain: number;
    trains: number;
    /** World-memory marks currently standing (M25). */
    marks: number;
    racers: {
      idx: number;
      s: number;
      speed: number;
      fuel: number;
      boosting: boolean;
      draft: number;
    }[];
  } | null {
    if (this.racers.length === 0) return null;
    let leader = this.racers[0];
    for (const r of this.racers) if (r.s > leader.s) leader = r;
    const top = [...this.racers].sort((a, b) => b.s - a.s).slice(0, 12);
    // Pack-spread percentile gap (M20a telemetry): the liveliness-law
    // tripwire — a draft compression wave would collapse it.
    const byS = [...this.racers].sort((a, b) => a.s - b.s);
    // Draft trains (M22): maximal chains of s-consecutive racers whose
    // followers are all in a wake this decision.
    let maxTrain = 1;
    let trains = 0;
    let run = 1;
    for (let k = 1; k < byS.length; k++) {
      if (byS[k].draft > 1 || byS[k - 1].draft > 1) run++;
      else {
        if (run >= 3) trains++;
        maxTrain = Math.max(maxTrain, run);
        run = 1;
      }
    }
    if (run >= 3) trains++;
    maxTrain = Math.max(maxTrain, run);
    const p10 = byS[Math.floor((byS.length - 1) * 0.1)].s;
    const p90 = byS[Math.floor((byS.length - 1) * 0.9)].s;
    const heroRival = this.racers[0]?.rival ?? null;
    return {
      n: this.racers.length,
      leader: leader.idx,
      leaderCam: this.leaderCam,
      respawnBurst: this.respawnTimes.filter((t) => t > this.windT - 2).length,
      spreadP10P90: Number((p90 - p10).toFixed(1)),
      rival: heroRival
        ? { target: heroRival.target, valence: heroRival.valence }
        : null,
      rivalIgnitions: this.rivalIgnitions,
      maxTrain,
      trains,
      marks: [...this.scarCount.values()].reduce((a, b) => a + b, 0),
      racers: top.map((r) => ({
        idx: r.idx,
        s: Number(r.s.toFixed(1)),
        speed: Number(r.speed.toFixed(2)),
        fuel: Number(r.boost.fuel.toFixed(2)),
        boosting: r.boost.boosting,
        draft: Number(r.draft.toFixed(3)),
      })),
    };
  }

  builtRange(): [number, number] {
    const keys = [...this.built.keys()];
    return [Math.min(...keys), Math.max(...keys)];
  }

  get spawningCount(): number {
    return this.spawning.size;
  }

  get exitingCount(): number {
    return this.exiting.length;
  }

  get physicsActive(): boolean {
    return this.engine !== null;
  }

  get railDebugState(): readonly RailDebugEvent[] | null {
    return this.railDebug ? this.railDebugRecent : null;
  }

  get railDebugSummary(): RailDebugSummary | null {
    if (!this.railDebug) return null;
    return {
      candidateEvents: this.railDebugCandidateEvents,
      generatedWindows: this.railDebugGeneratedWindows,
      byPiece: [...this.railDebugPieces.values()].sort((a, b) =>
        b.events - a.events || a.pieceId.localeCompare(b.pieceId)
      ),
    };
  }

  /** Apply a built segment's presentation frame to a plan-space sample
   * (camera path, trail, decorative riders live in world space). */
  private presentSample(
    seg: BuiltSegment,
    pos: THREE.Vector3,
    tan: THREE.Vector3 | null,
  ): void {
    const fr = seg.frame;
    if (!fr) return;
    this.frameScratch[0] = pos.x;
    this.frameScratch[1] = pos.y;
    this.frameScratch[2] = pos.z;
    const f = fr.twist
      ? segmentFrameAt(fr, twistParam(fr, this.frameScratch))
      : fr.entry;
    affineApply(f, this.frameScratch, this.frameScratch);
    pos.set(this.frameScratch[0], this.frameScratch[1], this.frameScratch[2]);
    if (tan) {
      this.frameScratch[0] = tan.x;
      this.frameScratch[1] = tan.y;
      this.frameScratch[2] = tan.z;
      quatRotate(f.q, this.frameScratch, this.frameScratch);
      tan.set(
        this.frameScratch[0],
        this.frameScratch[1],
        this.frameScratch[2],
      );
    }
  }

  /** World point at global arc length s (clamped to the built window). */
  samplePoint(s: number, out: THREE.Vector3): boolean {
    const seg = this.segmentAt(s);
    if (!seg) return false;
    const u = Math.min(
      1,
      Math.max(0, (s - seg.startS) / seg.length),
    );
    seg.curve.getPointAt(u, out);
    this.presentSample(seg, out, null);
    return true;
  }

  /** Point + tangent at global arc length s (peer riders). */
  sampleFrame(s: number, pos: THREE.Vector3, tan: THREE.Vector3): boolean {
    const seg = this.segmentAt(s);
    if (!seg) return false;
    const u = Math.min(1, Math.max(0, (s - seg.startS) / seg.length));
    seg.curve.getPointAt(u, pos);
    seg.curve.getTangentAt(u, tan);
    this.presentSample(seg, pos, tan);
    return true;
  }

  /** Local up of the camera subject (M16 — the follow cam rolls with
   * the subject's gravity frame). */
  cameraUp(): readonly [number, number, number] {
    return this.camUp;
  }

  /** Solid channel pieces plus island MASS meshes of the built window.
   * Supports/vegetation/decor stay out: a crossing tree frond or thin
   * scaffold should not yank the follow boom. */
  cameraOccluders(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const seg of this.built.values()) {
      out.push(...seg.cameraOccluders);
      for (const d of seg.disposables) {
        for (const m of d.meshes) {
          if (m.userData.occluder) out.push(m);
        }
      }
    }
    return out;
  }

  /** A tunnel is useful context until its shell contains the lens. Fade
   * only that local shell; the track, marbles and next opening stay visible. */
  fadeCameraTunnels(dt: number): void {
    const a = 1 - Math.exp(-12 * Math.max(dt, 1e-4));
    for (const seg of this.built.values()) {
      for (const mesh of seg.cameraFadeMeshes) {
        const box = mesh.geometry.boundingBox;
        if (!box) continue;
        this.cameraLocal.copy(this.ctx.camera.position);
        mesh.worldToLocal(this.cameraLocal);
        const inside = box.containsPoint(this.cameraLocal);
        const materials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const material of materials) {
          const base = mesh.userData.cameraBaseOpacity as number;
          const target = inside ? base * 0.06 : base;
          material.opacity += (target - material.opacity) * a;
        }
      }
    }
  }

  /** Arc-length range currently sampleable (the built window). */
  private builtBand(): [number, number] {
    let lo = Infinity;
    let hi = -Infinity;
    for (const seg of this.built.values()) {
      lo = Math.min(lo, seg.startS);
      hi = Math.max(hi, seg.startS + seg.length);
    }
    return lo <= hi ? [lo, hi] : [0, 0];
  }

  private segmentAt(s: number): BuiltSegment | null {
    let best: BuiltSegment | null = null;
    for (const seg of this.built.values()) {
      if (s >= seg.startS - 1e-9 && s <= seg.startS + seg.length + 1e-9) {
        return seg;
      }
      // Clamp: remember the nearest built segment for out-of-window asks.
      if (!best || Math.abs(s - seg.startS) < Math.abs(s - best.startS)) {
        best = seg;
      }
    }
    return best;
  }

  private build(i: number): BuiltSegment {
    const data = this.world.get(i);
    this.recordRailDebugExposure(data);
    const fr = data.frame;
    const group = new THREE.Group();
    group.name = `segment:${i}`;
    // Gravity reorientation (M16): all segment data is PLAN-space; the
    // scene groups carry the presentation frame. A twist splits the
    // segment into an entry-framed group and an exit-framed group (the
    // run's own kit pieces were dropped — the corkscrew chute below is
    // the run's geometry).
    let postGroup: THREE.Group | null = null;
    if (fr) {
      group.quaternion.set(...fr.entry.q);
      group.position.set(...fr.entry.t);
      if (fr.twist) {
        postGroup = new THREE.Group();
        postGroup.name = `segment:${i}:post`;
        postGroup.quaternion.set(...fr.exit.q);
        postGroup.position.set(...fr.exit.t);
      }
    }
    const groupFor = (pos: readonly [number, number, number]): THREE.Group =>
      postGroup && fr!.twist && twistParam(fr!, pos) > fr!.twist.len / 2
        ? postGroup
        : group;
    const spinners: Spinner[] = [];
    const missing = new Set<string>();
    const cameraOccluders: THREE.Object3D[] = [];
    const cameraFadeMeshes: THREE.Mesh[] = [];
    const pipeColorOf = (p: Placement) =>
      // Zone surfaces READ as their own pipe color regardless of
      // chapter color (icy = ice-pale, gravity = violet-charged).
      p.zone === "icy"
        ? ICE_PIPE
        : p.zone === "gravity"
        ? GRAVITY_PIPE
        : p.zone === "boost"
        ? BOOST_PIPE
        : p.chapter === undefined
        ? null
        : this.chapterColor(p.chapter);
    if (postGroup) {
      instantiatePlacements(
        data.placements.filter((p) => groupFor(p.pos) === group),
        this.assets,
        group,
        spinners,
        missing,
        pipeColorOf,
      );
      instantiatePlacements(
        data.placements.filter((p) => groupFor(p.pos) === postGroup),
        this.assets,
        postGroup,
        spinners,
        missing,
        pipeColorOf,
      );
    } else {
      instantiatePlacements(
        data.placements,
        this.assets,
        group,
        spinners,
        missing,
        pipeColorOf,
      );
    }
    // Islands render INSTANCED with per-instance color variation (strata,
    // moss/rust accents, per-island foliage lean) — islandRender.ts. An
    // island lives wholly on one side of a twist (the run cells are
    // exclusively claimed), so its whole build lands in one group.
    const disposables = data.islands.map((isle) =>
      buildIslandInstanced(
        isle,
        this.assets,
        isle.placements.length > 0
          ? groupFor(isle.placements[0].pos as [number, number, number])
          : group,
        missing,
      )
    );
    // Floating void decor (M18b): instanced, flat in the frame-correct
    // group (the waves see direct children), physics/occlusion/shadow
    // free. Split across twist halves per anchor like placements.
    const floats: FloatBuild[] = [];
    const voidByGroup = new Map<THREE.Group, Placement[]>();
    for (const p of data.voidDecor) {
      const g = groupFor(p.pos as [number, number, number]);
      voidByGroup.set(g, [...(voidByGroup.get(g) ?? []), p]);
    }
    for (const [g, list] of voidByGroup) {
      const fb = buildVoidDecor(list, this.assets, g, missing);
      if (fb) floats.push(fb);
    }
    // Tunnel render variants (glass / wire) — position-hashed, visual
    // only, deterministic per seed.
    const entryTunnels = applyTunnelVariants(group, this.ctx.renderer);
    disposables.push(entryTunnels);
    cameraFadeMeshes.push(...entryTunnels.cameraFadeMeshes);
    if (postGroup) {
      const exitTunnels = applyTunnelVariants(postGroup, this.ctx.renderer);
      disposables.push(exitTunnels);
      cameraFadeMeshes.push(...exitTunnels.cameraFadeMeshes);
    }
    for (const id of missing) {
      console.warn(`[endless] unknown pieceId "${id}" — skipped`);
    }
    this.root.add(group);
    if (postGroup) this.root.add(postGroup);

    const curve = buildOpenCurve(data.path);
    const length = curve.getLength();
    if (this.engine) {
      let proxies = [
        // The shared channel builder (T7 parity — bench/tests/production
        // one truth), including the braid alt lane when present.
        ...segmentProxies(data),
        ...data.islands.flatMap((isle) => islandProxies(isle.placements)),
        ...bumperProxies(data.placements),
      ];
      // Present in world space: the twist-run ribbon corkscrews between
      // the frames; rigid downstream shapes rotate wholesale.
      if (fr) proxies = worldifyProxies(proxies, fr);
      this.engine.addSegment(i, proxies);
      // Dense rail (points/tangents/s/mods) for nearest-point progress and
      // the assist force — the shared law (rail.ts), one truth with the
      // headless tests.
      const rail = railForSegment(data, this.branchOf.get(i) === "alt");
      if (fr) attachFrames(rail, fr);
      this.rails.set(i, rail);
      // Both rails live for alt-carrying segments (M17): per-racer lane
      // choice needs the other lane resident, not rebuilt on demand.
      if (hasAltBranch(data)) {
        const altRail = railForSegment(data, true);
        if (fr) attachFrames(altRail, fr);
        this.altRails.set(i, altRail);
        if (hasBraid(data)) this.braidSegs.add(i);
        if (data.braidContOf !== undefined) {
          this.braidContOf.set(i, data.braidContOf);
        }
      }
      // Fan wind capsules (M16): derived from the fan placements —
      // deterministic, no rng. Ledge fans face the channel, so their
      // capsules cross it (lateral gusts); wind-hinted fans carry their
      // own tuning (boost corridors, lip updrafts). Reoriented segments
      // blow their wind in the presented frame.
      let volumes = fanVolumesOf(data.placements);
      if (fr) {
        volumes = volumes.map((v) => {
          const vf = fr.twist
            ? segmentFrameAt(fr, twistParam(fr, v.origin))
            : fr.entry;
          return {
            ...v,
            origin: affineApply(vf, v.origin) as [number, number, number],
            dir: quatRotate(vf.q, v.dir) as [number, number, number],
          };
        });
      }
      this.fanField.addSegment(i, volumes);
      // The twist chute visual: the SAME cross-section law as the
      // collision ribbon, extruded along the rail's framed stations —
      // what you ride is what you hit. Violet-charged like gravity
      // zones (the set-piece vocabulary already reads that way).
      if (fr?.twist) {
        // Outer shell wears the chapter the line ENTERED the twist with
        // (the nearest pre-twist placement's chapter).
        let chapter: number | undefined;
        let bestT = -Infinity;
        for (const p of data.placements) {
          const t = twistParam(fr, p.pos);
          if (t < 0.5 && t > bestT && p.chapter !== undefined) {
            bestT = t;
            chapter = p.chapter;
          }
        }
        const mesh = this.buildTwistChute(
          rail,
          fr,
          chapter !== undefined ? this.chapterColor(chapter) : null,
        );
        if (mesh) {
          mesh.userData.cameraOccluder = true;
          cameraOccluders.push(mesh);
          this.root.add(mesh);
          disposables.push({
            meshes: [],
            dispose: () => {
              // Geometry only — the chute SHARES the kit's materials
              // (that's what makes it indistinguishable); disposing them
              // would strip every placed piece.
              this.root.remove(mesh);
              mesh.geometry.dispose();
            },
          } as unknown as InstancedBuild);
        }
      }
    }
    const prev = this.sMeta.get(i - 1);
    const startS = i === 0 ? 0 : (prev ? prev.start + prev.len : 0);
    if (i > 0 && !prev) {
      throw new Error(`segment ${i} built before its predecessor`);
    }
    this.sMeta.set(i, { start: startS, len: length });

    const seg: BuiltSegment = {
      index: i,
      group,
      curve,
      startS,
      length,
      spinners,
      islands: data.islands.length,
      entry: {
        x: data.plan.entry.x,
        y: data.plan.entry.y,
        z: data.plan.entry.z,
        dir: data.plan.entry.dir,
      },
      disposables,
      floats,
      lowY: Math.min(...data.plan.cells.map((c) => c.baseY)) * 2,
      cameraOccluders,
      cameraFadeMeshes,
      ...(fr ? { frame: fr } : {}),
      ...(postGroup ? { postGroup } : {}),
    };
    for (const g of postGroup ? [group, postGroup] : [group]) {
      for (const child of g.children) {
        if (child.userData.placementRole === "track") {
          cameraOccluders.push(child);
        }
      }
    }
    this.built.set(i, seg);
    this.islandsSeen += data.islands.length;
    // Streamed-in segments assemble as a wave flowing along the travel
    // direction; the session's opening fill appears whole.
    if (!this.initialBuild) {
      const entry = data.plan.entry;
      const step = [[1, 0], [0, 1], [-1, 0], [0, -1]][entry.dir];
      this.spawning.set(
        i,
        new SpawnAnim(
          seg.group,
          new THREE.Vector3(
            entry.x * TRACK_SCALE,
            entry.y * TRACK_SCALE,
            entry.z * TRACK_SCALE,
          ),
          new THREE.Vector3(step[0], 0, step[1]),
        ),
      );
    }
    return seg;
  }

  /** The reorient run's chute: the kit channel's SLICED cross-section
   * (twistChute.ts — the kit `straight` piece's own section, UVs and
   * shared materials) extruded along the rail's FRAMED stations. Rows
   * end EXACTLY on the run's boundary stations so the chute sits flush
   * against the neighboring kit pieces (an overlap would z-fight — the
   * surfaces are identical by construction). Chapter pipe recolor
   * applies like any placed piece. */
  private buildTwistChute(
    rail: Rail,
    fr: SegmentFrame,
    shellRgb: readonly [number, number, number] | null,
  ): THREE.Mesh | null {
    const section = extractChannelSection(this.assets);
    if (!section) return null;
    const tw = fr.twist!;
    const frames = rail.frames;
    if (!Array.isArray(frames)) return null;
    const rows: number[] = [];
    for (let i = 0; i < rail.pts.length; i++) {
      const t = twistParam(fr, rail.pts[i]);
      if (t < -1e-6 || t > tw.len + 1e-6) continue;
      rows.push(i);
    }
    if (rows.length < 2) return null;
    const S = TRACK_SCALE;
    const scratch: [number, number, number] = [0, 0, 0];
    const mesh = buildChuteMesh(section, rows.length, (r, x, y, out) => {
      const i = rows[r];
      const p = rail.pts[i];
      const tan = rail.tans[i];
      // The run is straight and flat in plan space — lateral is the
      // horizontal perp of the tangent; the rail line rides the channel
      // floor, so the section drops by its own measured floor height.
      const nl = Math.hypot(tan[0], tan[2]) || 1;
      const ux = tan[2] / nl;
      const uz = -tan[0] / nl;
      scratch[0] = p[0] + ux * x * S;
      scratch[1] = p[1] + (y - section.floorY) * S;
      scratch[2] = p[2] + uz * x * S;
      const f = frames[i] ?? fr.entry;
      affineApply(f, scratch, scratch);
      out.set(scratch[0], scratch[1], scratch[2]);
    });
    if (shellRgb) this.assets.applyPipeColor(mesh, shellRgb);
    return mesh;
  }

  private disposeSegment(seg: BuiltSegment): void {
    this.built.delete(seg.index);
    this.spawning.delete(seg.index);
    this.engine?.removeSegment(seg.index);
    this.rails.delete(seg.index);
    this.altRails.delete(seg.index);
    this.braidSegs.delete(seg.index);
    this.braidContOf.delete(seg.index);
    this.scarCount.delete(seg.index);
    this.fanField.removeSegment(seg.index);
    // Sink away rather than pop out; the group leaves the scene when the
    // exit wave finishes. (Occupancy claims release much later — the
    // generator's keep window — so nothing can build into a sinking spot.)
    const entry = seg.entry;
    const step = [[1, 0], [0, 1], [-1, 0], [0, -1]][entry.dir];
    this.exiting.push({
      group: seg.group,
      anim: new ExitAnim(
        seg.group,
        new THREE.Vector3(
          entry.x * TRACK_SCALE,
          entry.y * TRACK_SCALE,
          entry.z * TRACK_SCALE,
        ),
        new THREE.Vector3(step[0], 0, step[1]),
      ),
      // Float builds ride along: ticking stops here (the segment leaves
      // this.built — ExitAnim owns the matrices), disposal happens when
      // the wave completes.
      disposables: [...seg.disposables, ...seg.floats],
    });
    if (seg.postGroup) {
      this.exiting.push({
        group: seg.postGroup,
        anim: new ExitAnim(
          seg.postGroup,
          new THREE.Vector3(
            entry.x * TRACK_SCALE,
            entry.y * TRACK_SCALE,
            entry.z * TRACK_SCALE,
          ),
          new THREE.Vector3(step[0], 0, step[1]),
        ),
        disposables: [],
      });
    }
  }

  /** Keep the render window [cur−BEHIND, cur+AHEAD] built. */
  private ensure(): void {
    const cur = this.currentIndex;
    const lo = Math.max(0, cur - BEHIND);
    const hi = cur + AHEAD;
    if (hi - lo + 2 > KEEP_SEGMENTS) {
      throw new Error("render window exceeds generator keep window");
    }
    let added = false;
    for (let i = lo; i <= hi; i++) {
      if (!this.built.has(i)) {
        this.build(i);
        added = true;
      }
    }
    for (const seg of [...this.built.values()]) {
      if (seg.index < lo || seg.index > hi) this.disposeSegment(seg);
    }
    if (added) {
      // Fit box skips void decor (M18b): a far-band shoal 40u off the
      // line would inflate the shadow fit and the camera focus radius.
      const box = new THREE.Box3();
      this.root.updateWorldMatrix(true, true);
      const expand = (o: THREE.Object3D): void => {
        if (o.userData.voidDecor) return;
        if ((o as THREE.Mesh).isMesh) box.expandByObject(o);
        for (const c of o.children) expand(c);
      };
      expand(this.root);
      if (!box.isEmpty()) {
        fitShadowsTo(this.ctx.sun, box);
        this.cams.frame(box);
        const size = box.getSize(new THREE.Vector3());
        this.focus = {
          center: box.getCenter(new THREE.Vector3()),
          radius: Math.max(size.x, size.z) * 0.75 + 4,
        };
      }
    }
  }

  update(dt: number): void {
    if (dt <= 0) return;
    // Gust clock (M19a): pulsed wind gates on accumulated SIM time — the
    // same dt stream advanceTime drives, so replays agree. Advance before
    // the movers so this frame's decisions see this frame's phase.
    this.windT += dt;
    this.fanField.setTime(this.windT);
    const prevS = this.marbleS;
    if (this.engine) this.physicsMove(dt);
    else this.kinematicMove(dt);

    const milestone = milestoneFor(prevS, this.marbleS);
    if (milestone !== null) {
      this.toast.show(
        `${milestone.toLocaleString("en-US")}u\n ` +
          `${this.islandsSeen} islands`,
      );
      this.audio.cue("milestone");
    }

    const next = this.segmentAt(this.marbleS);
    if (next && next.index !== this.currentIndex) {
      this.currentIndex = next.index;
      this.audio.cue("checkpoint");
      // Atmosphere zones: every ZONE_SEGMENTS segments the void's climate
      // crossfades to the next drawn mood+weather combo.
      const zone = Math.floor(this.currentIndex / ZONE_SEGMENTS);
      if (zone !== this.zone) {
        this.zone = zone;
        const za = drawZone(this.seed, zone, this.zoneBiome(zone));
        this.atmosphere.blendTo(za);
        this.audio.setMood(za.mood.name);
      }
    }
    this.ensure();
    this.atmosphere.update(dt, this.focus);
    this.peerRing.update(
      dt,
      this.marbleS,
      this.builtBand(),
      (s, pos, tan) => this.sampleFrame(s, pos, tan),
    );
    this.finishFrame(dt);
  }

  /** Dominant island biome across a zone's segment span (M18a) — biases
   * the atmosphere draw toward the stretch's climate. Generation is a
   * pure function of the seed, so peeking ≤ZONE_SEGMENTS ahead of the
   * marble is deterministic and harmless; ensure() will build the same
   * segments moments later. Ties break toward the FIRST biome seen (the
   * one the marble meets first). */
  private zoneBiome(zone: number): string | undefined {
    const counts = new Map<string, number>();
    for (let i = zone * ZONE_SEGMENTS; i < (zone + 1) * ZONE_SEGMENTS; i++) {
      if (i < 0) continue;
      try {
        for (const isle of this.world.get(i).islands) {
          counts.set(isle.biome, (counts.get(isle.biome) ?? 0) + 1);
        }
      } catch {
        break; // never let atmosphere bias surface a planner throw
      }
    }
    // Max count; ties break toward the biome seen first (Map order).
    let best: string | undefined;
    let bestN = 0;
    for (const [b, n] of counts) {
      if (n > bestN) {
        best = b;
        bestN = n;
      }
    }
    return best;
  }

  /** Kinematic rail rider (?phys=0): the pre-M10 driver. */
  private kinematicMove(dt: number): void {
    const seg = this.segmentAt(this.marbleS);
    if (!seg) return;
    const u = Math.min(
      1,
      Math.max(0, (this.marbleS - seg.startS) / seg.length),
    );
    seg.curve.getTangentAt(u, this.tangent);
    this.speed +=
      (-this.tangent.y * GRAVITY - DRAG * (this.speed - BASE_SPEED)) *
      dt;
    this.speed = Math.min(MAX_SPEED, Math.max(MIN_SPEED, this.speed));
    this.marbleS += this.speed * dt;
    if (!this.samplePoint(this.marbleS, this.point)) return;
    this.marbleObj.position.copy(this.point);
    this.axis.copy(this.up).cross(this.tangent);
    if (this.axis.lengthSq() > 1e-8) {
      this.axis.normalize();
      this.spin.setFromAxisAngle(this.axis, (this.speed * dt) / this.radius);
      this.pivot.quaternion.premultiply(this.spin);
    }
  }

  // --- physics LOD (pack-scale realtime) -----------------------------------

  /** Racers the last LOD pass kept as real bodies (reused set). */
  private readonly liveSet = new Set<Racer>();
  private liveCount = 0;
  private lodClock = 0;

  /** Re-elect the real-body band around the camera subject: nearest
   * LIVE_CAP racers by progress within the retention window. Hysteresis
   * lives in the two radii (promote < PROMOTE_S, retain to DEMOTE_S). */
  private lodUpdate(): void {
    const n = this.racers.length;
    if (n <= LOD_ENGAGE_N || !this.engine) {
      this.liveCount = n;
      return;
    }
    const subj = this.racers[this.followedIdx] ?? this.racers[0];
    const subjS = subj.s;
    // sorted is ascending in s — find the subject's rank, expand outward.
    let lo = 0;
    let hi = this.sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.sorted[mid].s < subjS) lo = mid + 1;
      else hi = mid;
    }
    this.liveSet.clear();
    let a = lo - 1;
    let b = lo;
    while (this.liveSet.size < LIVE_CAP) {
      const da = a >= 0 ? subjS - this.sorted[a].s : Infinity;
      const db = b < this.sorted.length ? this.sorted[b].s - subjS : Infinity;
      if (da === Infinity && db === Infinity) break;
      if (da <= db) {
        if (da > LOD_DEMOTE_S) a = -1;
        else this.liveSet.add(this.sorted[a--]);
      } else {
        if (db > LOD_DEMOTE_S) b = this.sorted.length;
        else this.liveSet.add(this.sorted[b++]);
      }
    }
    for (const r of this.racers) {
      const keep = this.liveSet.has(r) || r.idx === 0 ||
        r.idx === this.followedIdx;
      if (r.lod === "live" && !keep) this.demoteRacer(r);
      else if (
        r.lod === "rider" && keep &&
        Math.abs(r.s - subjS) < LOD_PROMOTE_S
      ) this.promoteRacer(r);
      else if (
        r.lod === "rider" && (r.idx === 0 || r.idx === this.followedIdx)
      ) {
        this.promoteRacer(r);
      }
    }
    this.liveCount = 0;
    for (const r of this.racers) if (r.lod === "live") this.liveCount++;
  }

  /** Real body → kinematic rider: the far racer keeps racing on rails. */
  private demoteRacer(r: Racer): void {
    this.logRailDebug("lod-distance", r, this.railDebugTile(r));
    this.engine!.destroyMarble(r.idx);
    r.lod = "rider";
    r.cachedF = null;
    r.ghostT = 0;
    r.stallClock = 0;
    r.draft = 1;
    r.otkBlend = 0;
    r.blockBlend = 0;
    r.temperClock = 0;
    if (r.boost.boosting) r.boost = { fuel: r.boost.fuel, boosting: false };
    if (r.wasBoosting) {
      r.wasBoosting = false;
      if (this.peerMesh && r.idx > 0) {
        this.peerMesh.setColorAt(r.idx - 1, this.peerColors[r.idx - 1]);
        if (this.peerMesh.instanceColor) {
          this.peerMesh.instanceColor.needsUpdate = true;
        }
      }
    }
  }

  /** Rider → real body: spawned on its rail line at its glide speed,
   * briefly ghosted so re-entry into the pack never detonates it. */
  private promoteRacer(r: Racer): void {
    const engine = this.engine;
    if (!engine) return;
    const seg = this.segmentAt(r.s);
    const rail = seg ? this.railFor(r, seg.index) : undefined;
    if (!seg || !rail) return;
    const local = r.s - seg.startS;
    let idx = 0;
    while (idx < rail.s.length - 1 && rail.s[idx] < local) idx++;
    const t = rail.tans[idx];
    const nl = Math.hypot(t[2], t[0]) || 1;
    const lane = ((r.idx % 5) - 2) * 0.13;
    const pos: [number, number, number] = [
      rail.pts[idx][0] + (t[2] / nl) * lane,
      rail.pts[idx][1] + 0.3,
      rail.pts[idx][2] + (-t[0] / nl) * lane,
    ];
    const vel: [number, number, number] = [
      t[0] * r.speed,
      t[1] * r.speed,
      t[2] * r.speed,
    ];
    const f = railFrameAt(rail, idx);
    if (f) {
      affineApply(f, pos, pos);
      quatRotate(f.q, vel, vel);
    }
    engine.spawnMarble(r.idx, pos, vel);
    engine.setGhost(r.idx, true);
    r.ghostT = GHOST_T - 1.2; // short window — it spawns ON its own line
    r.lod = "live";
    r.nearest = { seg: seg.index, idx };
    r.lastProgressS = r.s;
    r.stallClock = 0;
    r.stallHopped = false;
    r.draft = 1;
    r.otkBlend = 0;
    r.blockBlend = 0;
    r.temperClock = 0;
  }

  /** Kinematic rider glide: advance progress at a personality cruise and
   * sit the marble on its (presented) rail line. Cheap by construction —
   * this is what lets N=9999 run realtime. */
  private riderUpdate(r: Racer, dt: number): void {
    const h = (((r.idx * 2654435761) >>> 0) % 1024) / 1024;
    let target = 3.0 + h * 1.2;
    // Rider draft fake (M20a): the far pack expresses the same wake law
    // kinematically, CAPPED — chained riders must never compound into a
    // runaway glide (the streaming anchor is the hero alone).
    const ahead = this.sorted[r.rank + 1];
    if (ahead && ahead !== r) {
      target *= Math.min(
        draftFactor(ahead.s - r.s, r.pilot.wake),
        RIDER_DRAFT_CAP,
      );
    }
    r.speed += (target - r.speed) * Math.min(1, dt * 0.5);
    r.s += r.speed * dt;
    const seg = this.segmentAt(r.s);
    if (!seg) return;
    // Braid entry (M20a): riders draw the SAME per-racer lane law as
    // live racers, so the fork split ratio holds at LOD distance. V3:
    // a continuation carries the fork's lane across the boundary.
    if (this.braidSegs.has(seg.index) && r.laneBraid !== seg.index) {
      const contOf = this.braidContOf.get(seg.index);
      if (contOf === undefined || r.laneBraid !== contOf) {
        r.laneAlt = braidLaneAlt(
          this.seed,
          r.idx,
          contOf ?? seg.index,
          r.pilot.lanePref,
        );
      }
      r.laneBraid = seg.index;
      r.nearest = { seg: seg.index, idx: 0 };
    }
    const rail = this.railFor(r, seg.index);
    if (!rail) return;
    const local = r.s - seg.startS;
    let idx = r.nearest.seg === seg.index ? r.nearest.idx : 0;
    while (idx < rail.s.length - 1 && rail.s[idx] < local) idx++;
    while (idx > 0 && rail.s[idx - 1] > local) idx--;
    r.nearest = { seg: seg.index, idx };
    const t = rail.tans[idx];
    const nl = Math.hypot(t[2], t[0]) || 1;
    const lane = ((r.idx % 5) - 2) * 0.13;
    const p = this.frameScratch;
    p[0] = rail.pts[idx][0] + (t[2] / nl) * lane;
    p[1] = rail.pts[idx][1] + 0.1;
    p[2] = rail.pts[idx][2] + (-t[0] / nl) * lane;
    const f = railFrameAt(rail, idx);
    if (f) affineApply(f, p, p);
    r.pos[0] = p[0];
    r.pos[1] = p[1];
    r.pos[2] = p[2];
  }

  /** One control decision for a racer (called at the decimated cadence):
   * nearest station, boost economy, autopilot force, jump — the result
   * lands in r.cachedF. `K` is the decision period in fixed steps. */
  private decide(r: Racer, K: number): void {
    const engine = this.engine!;
    if (r.lod !== "live") {
      r.cachedF = null;
      return;
    }
    this.updateNearestFor(r, r.pos);
    this.braidLaneFor(r);
    const rail = this.railFor(r, r.nearest.seg);
    if (!rail) {
      r.cachedF = null;
      return;
    }
    engine.setTrackSegment(r.idx, r.nearest.seg);
    engine.setRestitution(r.idx, stationRestitution(rail, r.nearest.idx));
    const nextRail = this.railFor(r, r.nearest.seg + 1) ?? null;
    // M16 conjugation: on a reoriented segment the control laws run in
    // PLAN space — marble state maps in through the station frame, the
    // decided force maps back out. The autopilot never sees the wall.
    const frame = railFrameAt(rail, r.nearest.idx);
    let cpos: readonly [number, number, number] = r.pos;
    let cvel: readonly [number, number, number] = r.vel;
    if (frame) {
      cpos = affineUnapply(frame, r.pos, this.planPos);
      cvel = quatUnrotate(frame.q, r.vel, this.planVel);
    }
    let f: [number, number, number] | null;
    if (this.useAutopilot) {
      r.simT += FIXED_DT * K;
      // Rank-window rivals: nearest distinct racer ahead/behind, and up
      // to four separation partners within 2 units of progress.
      const k0 = this.sorted.indexOf(r);
      // Rubber-banding through pilot SKILL (never thrust): the smoothed
      // race-position fraction modulates corner bravery + jump eagerness
      // on the scratch pilot. sorted is ascending in s — index 0 trails.
      const n2 = this.sorted.length;
      const behind = n2 > 1 ? 1 - k0 / (n2 - 1) : 0.5;
      r.rubber += (behind - r.rubber) *
        Math.min(1, (FIXED_DT * K) / RUBBER_TAU);
      rubberMod(r.pilot, r.rubber, r.pilotEff);
      let gapAhead: number | null = null;
      let gapBehind: number | null = null;
      let aheadRacer: Racer | null = null;
      let behindRacer: Racer | null = null;
      const grudge = r.rival?.valence === "grudge" ? r.rival : null;
      const ally = r.rival?.valence === "ally" ? r.rival : null;
      this.othersScratch.length = 0;
      const addNeighbor = (o: Racer) => {
        const slot = this.neighborPool[this.othersScratch.length];
        if (frame) {
          affineUnapply(frame, o.pos, slot.pos);
        } else {
          slot.pos[0] = o.pos[0];
          slot.pos[1] = o.pos[1];
          slot.pos[2] = o.pos[2];
        }
        // Separation asymmetry (M20b): the pilot shoves through its
        // grudge target more readily — the asymmetry lives entirely in
        // what the caller REPORTS; the autopilot compare is unchanged.
        slot.bravery = grudge && o.idx === grudge.target
          ? o.pilotEff.bravery - 0.1 * grudge.intensity
          : o.pilotEff.bravery;
        this.othersScratch.push(slot);
      };
      for (let k = k0 + 1; k < this.sorted.length && k <= k0 + 3; k++) {
        const o = this.sorted[k];
        const d = o.s - r.s;
        if (gapAhead === null && d > 0.05) {
          gapAhead = d;
          aheadRacer = o;
        }
        if (d < 2 && this.othersScratch.length < 4) addNeighbor(o);
      }
      for (let k = k0 - 1; k >= 0 && k >= k0 - 3; k--) {
        const o = this.sorted[k];
        const d = r.s - o.s;
        if (gapBehind === null && d > 0.05) {
          gapBehind = d;
          behindRacer = o;
        }
        if (d < 2 && this.othersScratch.length < 4) addNeighbor(o);
      }
      // Temper (M20b): no contact events in the shim — the deterministic
      // proxy is a peer inside contact distance this decision (plan
      // space, the same slots separation sees).
      // Real solver hits first (contact-events round), the proximity
      // proxy as the sub-threshold fallback.
      let contactNear = this.hitFlags.delete(r.idx);
      if (!contactNear) {
        for (const slot of this.othersScratch) {
          const dx = slot.pos[0] - cpos[0];
          const dy = slot.pos[1] - cpos[1];
          const dz = slot.pos[2] - cpos[2];
          if (dx * dx + dy * dy + dz * dz < CONTACT_NEAR * CONTACT_NEAR) {
            contactNear = true;
            break;
          }
        }
      }
      r.temperClock = temperStep(r.temperClock, FIXED_DT * K, contactNear);
      const heat = temperHeat(r.temperClock, r.pilot.temper);
      r.pilotEff.aggro *= heat.aggro;
      r.pilotEff.bravery *= heat.bravery;
      const headroom =
        r.dbg.vSafe !== undefined && r.dbg.wishspeed !== undefined
          ? r.dbg.vSafe - r.dbg.wishspeed
          : null;
      // Rivalry boost bias (M20b), levers only — boostStep unchanged:
      // a grudge target ahead reads CLOSER (widened attack range); an
      // ally ahead never triggers an attack (defend/overflow untouched).
      const aheadIsGrudge = grudge !== null && aheadRacer !== null &&
        aheadRacer.idx === grudge.target;
      const gapAheadEff = aheadIsGrudge
        ? gapAhead! / (1 + grudge!.intensity)
        : ally !== null && aheadRacer !== null && aheadRacer.idx === ally.target
        ? null
        : gapAhead;
      // Drafting (M20a): wake bonus behind the SAME-LANE leader — on a
      // braid, a wake across lanes is fiction; elsewhere lanes coincide.
      // Held at decision cadence (the boost-state 1-step-lag contract).
      const sameLane = aheadRacer === null ||
        !this.braidSegs.has(r.nearest.seg) ||
        (aheadRacer.laneAlt === r.laneAlt &&
          aheadRacer.laneBraid === r.laneBraid);
      r.draft = sameLane ? draftFactor(gapAhead, r.pilot.wake) : 1;
      // M22 slipstream economy: wake time pays fuel (boostStep refill).
      const draftDepth = (r.draft - 1) / DRAFT_GAIN;
      const wasBoostingNow = r.boost.boosting;
      r.boost = boostStep(
        r.boost,
        r.pilotEff,
        FIXED_DT * K,
        headroom,
        gapAheadEff,
        gapBehind,
        draftDepth,
      );
      if (!wasBoostingNow && r.boost.boosting && aheadIsGrudge) {
        this.rivalIgnitions++;
      }
      // Overtake commitment (M20a): while the burn is an ATTACK (rival
      // ahead in range), hold an off-center passing line on the pilot's
      // chirality side. Hysteresis (engage < 3.5u, release > 5u) plus a
      // ~0.4s slew keep the single offset actuator from flapping against
      // separation steering; the offset stays inside CORRIDOR_DEAD so
      // the corridor spring never fights it. Scratch pilot only.
      const otkEngaged = r.boost.boosting && gapAhead !== null &&
        (r.otkBlend > 0 ? gapAhead < OTK_DISENGAGE : gapAhead < OTK_ENGAGE);
      r.otkBlend += ((otkEngaged ? 1 : 0) - r.otkBlend) *
        Math.min(1, (FIXED_DT * K) / OTK_SLEW_TAU);
      if (r.otkBlend < 0.02 && !otkEngaged) r.otkBlend = 0;
      // Defensive blocking (M20b): the grudge target chasing close gets
      // its line stood in — offset biased toward the chaser's side, same
      // hysteresis + slew, strictly BELOW the overtake in priority (you
      // can't block while attacking; one offset actuator, one owner).
      let blockOff = 0;
      const chaserIsGrudge = grudge !== null && behindRacer !== null &&
        behindRacer.idx === grudge.target;
      const blockEngaged = !otkEngaged && chaserIsGrudge &&
        gapBehind !== null &&
        gapBehind <
          BLOCK_RANGE * r.pilotEff.aggro * (1 + grudge!.intensity) *
            (r.blockBlend > 0 ? 1.3 : 1);
      if (chaserIsGrudge || r.blockBlend > 0) {
        const t = rail.tans[r.nearest.idx];
        if (behindRacer && t) {
          const cp = frame
            ? affineUnapply(frame, behindRacer.pos, this.frameScratch)
            : behindRacer.pos;
          const side = (cp[0] - cpos[0]) * t[2] - (cp[2] - cpos[2]) * t[0];
          blockOff = (side >= 0 ? 1 : -1) * BLOCK_OFF;
        }
      }
      r.blockBlend += ((blockEngaged ? 1 : 0) - r.blockBlend) *
        Math.min(1, (FIXED_DT * K) / OTK_SLEW_TAU);
      if (r.blockBlend < 0.02 && !blockEngaged) r.blockBlend = 0;
      const wander = pilotOffset(r.pilot, r.simT);
      let offset = wander * (1 - r.blockBlend) + blockOff * r.blockBlend;
      offset = offset * (1 - r.otkBlend) +
        r.pilot.chir * OVERTAKE_OFF * r.otkBlend;
      r.pilotEff.offset = offset;
      f = autopilotForce(
        rail,
        r.nearest.idx,
        cpos,
        cvel,
        r.mass,
        nextRail,
        r.pilotEff,
        r.dbg,
        this.othersScratch,
        r.boost.boosting,
        r.draft,
      );
      // M25 ice scuffs: hard braking on an icy surface leaves marks —
      // rate-limited per racer, deterministic (sim state only).
      if (
        r.dbg.mode === "brake" &&
        rail.mods[r.nearest.idx]?.icy === true &&
        r.simT - r.lastScuffT > 0.5
      ) {
        r.lastScuffT = r.simT;
        this.pendingScars.push({
          x: r.pos[0],
          y: r.pos[1],
          z: r.pos[2],
          speed: 0, // scuff, not knock
          idx: r.idx,
        });
        if (this.pendingScars.length > 64) this.pendingScars.shift();
      }
      const j = wantJump(
        rail,
        r.nearest.idx,
        cpos,
        cvel,
        nextRail,
        r.pilotEff,
      );
      if (j) {
        const n = frame ? quatRotate(frame.q, j[0], this.frameScratch) : j[0];
        engine.jump(r.idx, n, j[1]);
        if (!r.wasHop && r.idx === 0) this.audio.cue("launch");
        r.wasHop = true;
      } else r.wasHop = false;
    } else {
      f = stationForce(
        rail,
        r.nearest.idx,
        cpos,
        cvel,
        r.mass,
        nextRail,
      );
    }
    // Rotate the plan-space control force into the presented world.
    if (f && frame) {
      f = quatRotate(frame.q, f, this.planF) as [number, number, number];
    }
    // Gravity zones as per-marble delta forces (world gravity is global
    // and fixed — one vector cannot serve N marbles).
    const gd = stationGravityDelta(rail, r.nearest.idx, r.mass);
    // Fan wind (M16): fans are force-field volumes — accumulate the
    // capsule forces near the racer's segment (O(nearby), no alloc).
    const w = this.windScratch;
    w[0] = 0;
    w[1] = 0;
    w[2] = 0;
    this.fanField.forceNear(r.nearest.seg, r.pos, r.mass, w);
    const fx = (f?.[0] ?? 0) + (gd?.[0] ?? 0) + w[0];
    const fy = (f?.[1] ?? 0) + (gd?.[1] ?? 0) + w[1];
    const fz = (f?.[2] ?? 0) + (gd?.[2] ?? 0) + w[2];
    r.cachedF = fx !== 0 || fy !== 0 || fz !== 0 ? [fx, fy, fz] : null;
  }

  /** Box3D driver (M15): N real marbles — the hero plus racer peers —
   * each running its own autopilot; real falls, per-marble respawn,
   * leader-cam. */
  private physicsMove(dt: number): void {
    const engine = this.engine!;
    const n = this.racers.length;
    // Pack-scale controls: DECISIONS (nearest scan, sweep, boost, jump)
    // recompute round-robin every K steps while the cached force applies
    // every step. With physics LOD the cadence keys on the LIVE body
    // count (riders cost nothing) — feel near the camera stays crisp.
    const liveN = this.liveCount || n;
    const K = liveN <= 64 ? 1 : liveN <= 256 ? 2 : liveN <= 1024 ? 4 : 8;
    engine.step(dt, () => {
      // Contact hit events (contact-events round): real knocks from the
      // solver, per step (events clear on the next step — read them
      // inside the callback). Marble-marble hits arm the TEMPER trigger
      // (upgrading the proximity proxy); marble-static hits above the
      // scar threshold queue world-memory marks (M25).
      const hits = engine.hits();
      for (let h = 0; h < hits.count; h++) {
        const a = hits.data[h * 6];
        const b = hits.data[h * 6 + 1];
        const speed = hits.data[h * 6 + 2];
        if (a >= 0 && b >= 0) {
          this.hitFlags.add(a);
          this.hitFlags.add(b);
        } else if (speed >= SCAR_SPEED) {
          const solo = a >= 0 ? a : b;
          this.pendingScars.push({
            x: hits.data[h * 6 + 3],
            y: hits.data[h * 6 + 4],
            z: hits.data[h * 6 + 5],
            speed,
            idx: solo,
          });
          if (this.pendingScars.length > 64) this.pendingScars.shift();
        }
      }
      // ONE wasm call for the whole pack, copied into persistent
      // per-racer scratch (no per-step allocation). Riders keep their
      // kinematic state — their slots read back zeros.
      const flat = engine.statesAll(n);
      for (let i = 0; i < n; i++) {
        const r = this.racers[i];
        if (r.lod !== "live") continue;
        const b = i * 10;
        r.pos[0] = flat[b];
        r.pos[1] = flat[b + 1];
        r.pos[2] = flat[b + 2];
        r.vel[0] = flat[b + 3];
        r.vel[1] = flat[b + 4];
        r.vel[2] = flat[b + 5];
        r.quat[0] = flat[b + 6];
        r.quat[1] = flat[b + 7];
        r.quat[2] = flat[b + 8];
        r.quat[3] = flat[b + 9];
      }
      // Progress-sorted order: rival gaps + separation partners come
      // from rank neighbors — O(N log N), not O(N²).
      this.sorted.length = 0;
      for (const r of this.racers) this.sorted.push(r);
      this.sorted.sort((a, b2) => a.s - b2.s);
      // Rank stamp (M20a): riders read their leader through it — one int
      // store per racer, no extra alloc.
      for (let ri = 0; ri < this.sorted.length; ri++) {
        this.sorted[ri].rank = ri;
      }
      this.stepCounter++;
      // Physics LOD: re-elect the real-body band around the subject.
      if (++this.lodClock >= LOD_PERIOD) {
        this.lodClock = 0;
        this.lodUpdate();
      }
      for (let i = 0; i < n; i++) {
        const r = this.racers[i];
        if (r.lod !== "live") continue;
        if ((this.stepCounter + i) % K === 0) {
          this.decide(r, K);
        }
        const cf = r.cachedF;
        if (cf && (cf[0] !== 0 || cf[1] !== 0 || cf[2] !== 0)) {
          engine.force(r.idx, cf[0], cf[1], cf[2]);
        }
      }
    });

    let lowest = Infinity;
    for (const b of this.built.values()) {
      lowest = Math.min(lowest, b.lowY);
    }
    const windowLo = Math.min(...this.built.keys());
    const windowLoS = this.built.get(windowLo)?.startS ?? 0;

    let heroFell = false;
    const flat = engine.statesAll(this.racers.length);
    for (const [ri, r] of this.racers.entries()) {
      const b = ri * 10;
      // Kinematic riders (physics LOD): glide the rail, write the
      // matrix, redistribute if the window dropped them — none of the
      // body bookkeeping below applies.
      if (r.lod === "rider") {
        this.riderUpdate(r, dt);
        if (this.peerMesh && r.idx > 0) {
          this.instPos.set(r.pos[0], r.pos[1], r.pos[2]);
          this.instQuat.set(r.quat[0], r.quat[1], r.quat[2], r.quat[3]);
          this.instScale.setScalar(1);
          this.instMatrix.compose(this.instPos, this.instQuat, this.instScale);
          this.peerMesh.setMatrixAt(r.idx - 1, this.instMatrix);
          this.peerShellMesh?.setMatrixAt(r.idx - 1, this.instMatrix);
        }
        if (r.s < windowLoS - 2) this.respawnPeer(r, "stream-cull");
        continue;
      }
      r.pos[0] = flat[b];
      r.pos[1] = flat[b + 1];
      r.pos[2] = flat[b + 2];
      r.speed = Math.hypot(flat[b + 3], flat[b + 4], flat[b + 5]);
      // Respawn spawn-in: ease the visual ~0.1 → 1 instead of popping.
      // The group path scales r.obj (the kit mesh inside keeps its own
      // MARBLE_SCALE); the instanced path folds the scale into the
      // instance matrix. Allocation-free — scalar math only.
      let spawnScale = 1;
      const spawnAnimating = r.spawnAnim > 0;
      if (spawnAnimating) {
        r.spawnAnim = Math.max(0, r.spawnAnim - dt);
        const k = 1 - r.spawnAnim / SPAWN_IN_T;
        spawnScale = 0.1 + 0.9 * (1 - (1 - k) * (1 - k) * (1 - k));
      }
      if (r.obj && r.pivot) {
        r.obj.position.set(flat[b], flat[b + 1], flat[b + 2]);
        if (spawnAnimating) r.obj.scale.setScalar(spawnScale);
        r.pivot.quaternion.set(
          flat[b + 6],
          flat[b + 7],
          flat[b + 8],
          flat[b + 9],
        );
      } else if (this.peerMesh) {
        // Instanced peer: one matrix write into the shared mesh.
        this.instPos.set(flat[b], flat[b + 1], flat[b + 2]);
        this.instQuat.set(
          flat[b + 6],
          flat[b + 7],
          flat[b + 8],
          flat[b + 9],
        );
        // instScale is shared scratch — set it EVERY racer (1 when not
        // animating), or one spawning racer shrinks the next N instances.
        this.instScale.setScalar(spawnScale);
        this.instMatrix.compose(this.instPos, this.instQuat, this.instScale);
        this.peerMesh.setMatrixAt(r.idx - 1, this.instMatrix);
        this.peerShellMesh?.setMatrixAt(r.idx - 1, this.instMatrix);
      }
      // Respawn immunity: ghosted marbles pass through neighbors (track
      // stays solid). Tick down; resolidify only once CLEAR of the pack,
      // with a hard cap (GHOST_MAX) so nobody ghosts forever.
      if (r.ghostT !== 0) {
        r.ghostT -= dt;
        if (
          r.ghostT <= GHOST_T - GHOST_MAX ||
          (r.ghostT <= 0 && this.clearOfPack(r))
        ) {
          engine.setGhost(r.idx, false);
          r.ghostT = 0;
        }
      }
      const seg = this.built.get(r.nearest.seg);
      const rail = this.railFor(r, r.nearest.seg);
      if (seg && rail) {
        // Alt-lane s runs on the alt rail's own arc length (≈ main ±
        // wander) — leaderboard tolerance is fine (brief rule).
        r.s = Math.max(r.s, seg.startS + rail.s[r.nearest.idx]);
      }

      // Boost visual is a separate hot trail on the camera subject. Keep
      // the marble's authored materials intact (no full-sphere color filter).
      if (r.boost.boosting !== r.wasBoosting) {
        r.wasBoosting = r.boost.boosting;
        if (r.boost.boosting && r.idx === this.followedIdx) {
          this.audio.cue("boost");
        }
      }

      if (r.idx === 0) {
        // Hero cues off the station modifier transitions (rule-derived).
        const mod = rail?.mods[r.nearest.idx] ?? null;
        const flight = !!mod?.flight;
        if (flight && !this.wasFlight) this.audio.cue("launch");
        if (!flight && this.wasFlight) this.audio.cue("land");
        this.wasFlight = flight;
        const surge = (mod?.speed !== undefined && mod.speed > 1) ||
          mod?.tilt !== undefined;
        if (surge && !this.wasSurge) this.audio.cue("boost");
        this.wasSurge = surge;
        this.nearest = r.nearest;
        this.marbleS = Math.max(this.marbleS, r.s);
      }

      // Falls + stalls, per marble. The hero keeps the checkpoint
      // escalation; peers respawn behind the pack tail. Reoriented
      // racers fall along their LOCAL down — compare in plan space
      // (lowY is plan-space by construction).
      const fFall = rail ? railFrameAt(rail, r.nearest.idx) : null;
      const planY = fFall
        ? affineUnapply(fFall, r.pos, this.frameScratch)[1]
        : r.pos[1];
      const fell = planY < lowest - 10;
      if (r.idx === 0) {
        if (fell) {
          heroFell = true;
          continue;
        }
        if (this.marbleS > this.lastProgressS + 0.4) {
          this.lastProgressS = this.marbleS;
          this.stallClock = 0;
          this.stallHopped = false;
        } else {
          this.stallClock += dt;
          // Escape hop (M14b): one visible attempt before the net.
          if (
            this.stallClock > 2.5 && !this.stallHopped && this.useAutopilot
          ) {
            this.stallHopped = true;
            engine.jump(
              0,
              fFall ? frameUp(fFall, this.frameScratch) : [0, 1, 0],
              4.2,
            );
            this.audio.cue("launch");
          }
          if (this.stallClock > 6) this.respawn("stall");
        }
        continue;
      }
      // Peer: fell, wedged, or dropped out of the keep window.
      if (r.s > r.lastProgressS + 0.4) {
        r.lastProgressS = r.s;
        r.stallClock = 0;
        r.stallHopped = false;
      } else {
        r.stallClock += dt;
        if (r.stallClock > 2.5 && !r.stallHopped && this.useAutopilot) {
          r.stallHopped = true;
          engine.jump(
            r.idx,
            fFall ? frameUp(fFall, this.frameScratch) : [0, 1, 0],
            4.2,
          );
        }
      }
      if (fell || r.stallClock > 6 || r.s < windowLoS - 2) {
        const reason: Exclude<RailDebugReason, "lod-distance"> = fell
          ? "fall"
          : r.stallClock > 6
          ? "stall"
          : "stream-cull";
        this.respawnPeer(r, reason);
      }
    }
    if (this.peerMesh) this.peerMesh.instanceMatrix.needsUpdate = true;
    if (this.peerShellMesh) {
      this.peerShellMesh.instanceMatrix.needsUpdate = true;
    }
    if (heroFell) {
      this.respawn("fall");
      return;
    }

    // Camera subject: the hero by default; the race leader when the user
    // elected leader-cam (SPACE). The trail follows the same subject.
    // Hysteresis on lead changes: neck-and-neck racers alternate the top
    // spot every few frames — the camera only hands over when someone
    // clearly leads the current subject (0.75u), or the shot flickers
    // and the trail resets endlessly.
    let followed = this.racers[0];
    if (this.leaderCam) {
      const current = this.racers.find((r) => r.idx === this.followedIdx) ??
        followed;
      let top = current;
      for (const r of this.racers) if (r.s > top.s) top = r;
      followed = top.s > current.s + 0.75 ? top : current;
    }
    const subjectChanged = this.followedIdx !== followed.idx;
    this.followedIdx = followed.idx;
    // Follow-cam local up (M16): the lens rolls with the subject's
    // gravity frame. Eased HERE as well as in camera.ts, and a missing
    // rail (mid-cull, mid-respawn) keeps the last known up — a one-frame
    // flip to world-up read as "snap to a default angle, float back"
    // (user report).
    const fRail = this.railFor(followed, followed.nearest.seg);
    if (fRail) {
      const fFrame = railFrameAt(fRail, followed.nearest.idx);
      if (fFrame) frameUp(fFrame, this.camUpTarget);
      else {
        this.camUpTarget[0] = 0;
        this.camUpTarget[1] = 1;
        this.camUpTarget[2] = 0;
      }
    }
    const ku = 1 - Math.exp(-6 * Math.max(dt, 1e-4));
    this.camUp[0] += (this.camUpTarget[0] - this.camUp[0]) * ku;
    this.camUp[1] += (this.camUpTarget[1] - this.camUp[1]) * ku;
    this.camUp[2] += (this.camUpTarget[2] - this.camUp[2]) * ku;
    const ul = Math.hypot(this.camUp[0], this.camUp[1], this.camUp[2]) || 1;
    this.camUp[0] /= ul;
    this.camUp[1] /= ul;
    this.camUp[2] /= ul;
    this.speed = followed.speed;
    // The WORLD anchors to the HERO: streaming, culling, milestones and
    // respawn fill all key on marbleS = hero progress (ratcheted in the
    // hero branch above). The camera subject only moves the lens — with
    // leader-cam ratcheting marbleS, a peer respawned into the forward
    // fill became the new leader, slid the window, forced stragglers to
    // respawn (some ahead again), and generation outchased the hero by
    // 10k+ units (user report).
    this.camS = followed.s;
    // Instanced peers have no scene object — a proxy stands in for the
    // camera and the trail.
    let fObj: THREE.Object3D;
    let fPivot: THREE.Object3D;
    if (followed.obj && followed.pivot) {
      fObj = followed.obj;
      fPivot = followed.pivot;
    } else {
      this.instProxy.position.set(
        followed.pos[0],
        followed.pos[1],
        followed.pos[2],
      );
      fObj = this.instProxy;
      fPivot = this.instProxy;
    }
    if (this.cameraProxy) {
      this.cameraProxy.obj = fObj;
      this.cameraProxy.s = this.camS;
      this.cameraProxy.speed = this.speed;
    }
    if (subjectChanged || this.followedPivot !== fPivot) {
      this.followedPivot = fPivot;
      this.trail.reset();
      this.boostTrail.reset();
      this.boostTrailFade = 0;
      this.boostTrailActive = false;
    }
  }

  get canFollowLeader(): boolean {
    return this.racers.length > 1;
  }

  /** SPACE toggle: hero-follow (default) ↔ leader-cam. */
  toggleLeaderCam(): void {
    if (!this.canFollowLeader) return;
    this.leaderCam = !this.leaderCam;
    if (this.racers.length > 0) {
      this.toast.show(
        this.leaderCam ? "Following the leader" : "Following your marble",
      );
    }
  }

  /** True when no other racer sits within GHOST_CLEAR_D of `r` — checked
   * against progress-rank neighbors (`this.sorted`, ordered by s): a
   * cheap stand-in for a spatial query at pack scale. Binary-search the
   * s-window, then scan it (capped — a mega-clump stops mattering past
   * the nearest ranks). Allocation-free. */
  private clearOfPack(r: Racer): boolean {
    const arr = this.sorted;
    const d2 = GHOST_CLEAR_D * GHOST_CLEAR_D;
    // First rank whose s is inside the window (arr is sorted by s).
    const loS = r.s - 0.5;
    const hiS = r.s + 0.5;
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid].s < loS) lo = mid + 1;
      else hi = mid;
    }
    for (let j = lo, seen = 0; j < arr.length && seen < 64; j++, seen++) {
      const o = arr[j];
      if (o.s > hiS) break;
      if (o === r) continue;
      const dx = o.pos[0] - r.pos[0];
      const dy = o.pos[1] - r.pos[1];
      const dz = o.pos[2] - r.pos[2];
      if (dx * dx + dy * dy + dz * dz < d2) return false;
    }
    return true;
  }

  /** Respawn a peer racer DISTRIBUTED across the pack's fill range (user
   * rule: liveliness is distribution, not a clump — a culled segment once
   * respawned every straggler onto one station). The range grows with
   * the pack size, filling backward AND forward of the camera subject,
   * clamped inside the built rails; each racer owns a deterministic slot
   * (golden-ratio hash of its idx) plus a lane, so simultaneous respawns
   * land spread out. No checkpoint escalation, no death counting. */
  private respawnPeer(
    r: Racer,
    reason: Exclude<RailDebugReason, "lod-distance">,
  ): void {
    const engine = this.engine;
    if (!engine) return;
    const debugFrom = this.railDebug ? this.railDebugTile(r) : null;
    let loS = Infinity;
    let hiS = -Infinity;
    for (const b of this.built.values()) {
      loS = Math.min(loS, b.startS);
      hiS = Math.max(hiS, b.startS + b.length);
    }
    const lo = loS + 2;
    const hi = hiS - 6;
    if (!(hi > lo)) return;
    // Fill range: ~10u for a duo, growing with the pack, capped by the
    // built window. 70% of it trails the camera subject, 30% runs ahead
    // (traffic to catch AND to be caught by).
    const span = Math.min(hi - lo, 10 + this.racers.length * 0.35);
    const h = (((r.idx * 2654435761) >>> 0) % 1024) / 1024;
    const heroS = this.racers[0]?.s ?? this.marbleS;
    const at = Math.max(lo, Math.min(hi, heroS + (h - 0.7) * span));
    // Fresh lane state: the respawn seats on the MAIN rail; if it lands
    // inside a braid, the next decide re-draws the (deterministic) lane.
    r.laneAlt = false;
    r.laneBraid = -1;
    r.draft = 1;
    r.otkBlend = 0;
    r.blockBlend = 0;
    r.temperClock = 0;
    // Pile-up telemetry (M20a): trailing-window respawn counter.
    this.respawnTimes.push(this.windT);
    while (
      this.respawnTimes.length > 0 && this.respawnTimes[0] < this.windT - 4
    ) {
      this.respawnTimes.shift();
    }
    const seg = this.segmentAt(at);
    const rail = seg ? this.rails.get(seg.index) : undefined;
    if (!seg || !rail) return;
    let idx = 0;
    while (idx < rail.s.length - 1 && rail.s[idx] < at - seg.startS) idx++;
    if (debugFrom) {
      this.logRailDebug(
        reason,
        r,
        debugFrom,
        this.railDebugTile(r, seg.index, idx, rail),
      );
    }
    // Kinematic riders (physics LOD) redistribute by progress alone —
    // no body to spawn; the next riderUpdate seats them on the rail.
    if (r.lod === "rider") {
      r.nearest = { seg: seg.index, idx };
      r.s = seg.startS + rail.s[idx];
      r.lastProgressS = r.s;
      r.stallClock = 0;
      return;
    }
    const t = rail.tans[idx];
    const nl = Math.hypot(t[2], t[0]) || 1;
    const lane = ((r.idx % 5) - 2) * 0.13;
    // Compose the drop in PLAN space (lane beside the line, a hop above
    // the floor), then present it through the station frame — reoriented
    // respawns materialize beside their wall-riding pack.
    const spawnPos: [number, number, number] = [
      rail.pts[idx][0] + (t[2] / nl) * lane,
      rail.pts[idx][1] + 0.4 + (r.idx % 7) * 0.12,
      rail.pts[idx][2] + (-t[0] / nl) * lane,
    ];
    const spawnVel: [number, number, number] = [t[0] * 2.2, 0, t[2] * 2.2];
    const sf = railFrameAt(rail, idx);
    if (sf) {
      affineApply(sf, spawnPos, spawnPos);
      quatRotate(sf.q, spawnVel, spawnVel);
    }
    engine.spawnMarble(r.idx, spawnPos, spawnVel);
    r.nearest = { seg: seg.index, idx };
    r.s = seg.startS + rail.s[idx];
    r.lastProgressS = r.s;
    r.stallClock = 0;
    r.stallHopped = false;
    // Materialize gently: scale-in envelope + collision immunity until
    // clear of the pack. setGhost must come AFTER spawnMarble — spawn
    // resets the shim shape (and the engine's ghost cache) to solid.
    r.spawnAnim = SPAWN_IN_T;
    engine.setGhost(r.idx, true);
    r.ghostT = GHOST_T;
  }

  /** Tinted marble visuals for a real racer peer (mirrors the ring's
   * buildVisual; scale stays MARBLE_SCALE so the mesh matches the shared
   * physics radius). */
  private buildRacerVisual(index: number): {
    obj: THREE.Group;
    pivot: THREE.Group;
    materials: THREE.Material[];
    baseEmissive: THREE.Color | null;
  } {
    const identity = racerIdentity(this.seed, index);
    const obj = new THREE.Group();
    obj.name = `racer:${index}`;
    const pivot = new THREE.Group();
    obj.add(pivot);
    const materials: THREE.Material[] = [];
    const mesh = this.assets.getPiece(identity.meshId) ??
      this.assets.getPiece(MARBLE_PIECE);
    if (mesh) {
      mesh.scale.setScalar(MARBLE_SCALE);
      // Empty kit shell → inject the opaque core (see marbleCoreMesh);
      // the traverse below tints it and applies the phenotype effect
      // like any other core material.
      addMarbleCore(mesh);
      // Always clone materials (boost glow needs its own emissive even
      // on untinted marbles). The phenotype layers MULTIPLY over the
      // base look: shell tints the color, the effect reshapes the
      // material (gloss/pearl/ember/ghost at its intensity).
      const tint = identity.tint;
      const { effect } = identity;
      mesh.traverse((child: THREE.Object3D) => {
        const m = child as THREE.Mesh;
        if (!m.isMesh) return;
        const cloneOne = (mat: THREE.Material): THREE.Material => {
          // The kit marble is core + shell: the "glass" material IS the
          // shell — it becomes real transmission glass tinted by the
          // phenotype (Beer–Lambert), while identity tint + effects
          // stay on the opaque CORE the glass refracts.
          if (mat.name === "glass") {
            // STABLE transparent order per racer: overlapping shells at
            // near-equal depth otherwise swap draw order frame-to-frame
            // (reads as z-fighting when marbles press together).
            m.renderOrder = 10 + index;
            const g = glassShellMaterial(this.ctx.renderer, identity);
            materials.push(g);
            return g;
          }
          const clone = (mat as THREE.MeshStandardMaterial).clone();
          if (tint) {
            clone.color.setRGB(tint[0] / 255, tint[1] / 255, tint[2] / 255);
          }
          const i = effect.intensity;
          if (effect.kind === "gloss") {
            clone.roughness *= 1 - 0.7 * i;
            clone.metalness = Math.min(1, clone.metalness + 0.35 * i);
            clone.envMap = marbleEnv(this.ctx.renderer);
            clone.envMapIntensity = 0.6;
          } else if (effect.kind === "pearl") {
            clone.roughness = 0.15;
            clone.metalness = 0.5 * i;
            clone.color.lerp(new THREE.Color(1, 1, 1), 0.25 * i);
            clone.envMap = marbleEnv(this.ctx.renderer);
            clone.envMapIntensity = 0.6;
          } else if (effect.kind === "ember") {
            clone.emissive = clone.color.clone().multiplyScalar(0.5 * i);
          } else if (effect.kind === "ghost") {
            clone.transparent = true;
            clone.opacity = 1 - 0.45 * i;
          }
          materials.push(clone);
          return clone;
        };
        m.material = Array.isArray(m.material)
          ? m.material.map(cloneOne)
          : cloneOne(m.material);
      });
      const box = new THREE.Box3().setFromObject(mesh);
      const center = box.getCenter(new THREE.Vector3());
      pivot.position.copy(center);
      mesh.position.sub(center);
      pivot.add(mesh);
    }
    this.ctx.scene.add(obj);
    const baseEmissive = identity.effect.kind === "ember"
      ? new THREE.Color().copy(
        (materials[0] as THREE.MeshStandardMaterial | undefined)?.emissive ??
          new THREE.Color(0, 0, 0),
      )
      : null;
    return { obj, pivot, materials, baseEmissive };
  }

  /** The rail a racer rides in a segment: the alt lane on its chosen
   * braid segment, the main (branchOf-resolved) rail everywhere else.
   * r.nearest.idx always indexes into THIS rail — every dereference of a
   * racer's station must come through here. */
  private railFor(r: Racer, segIdx: number): Rail | undefined {
    // V3: a racer whose lane was drawn at the FORK segment keeps riding
    // alt through the continuation segment (the boundary is mid-braid).
    if (
      r.laneAlt &&
      (r.laneBraid === segIdx || this.braidContOf.get(segIdx) === r.laneBraid)
    ) {
      return this.altRails.get(segIdx) ?? this.rails.get(segIdx);
    }
    return this.rails.get(segIdx);
  }

  /** Resolve a racer's dense station back to the authored tile/window. */
  private railDebugTile(
    r: Racer,
    segIdx = r.nearest.seg,
    stationIdx = r.nearest.idx,
    railOverride?: Rail,
  ): RailDebugTile {
    const rail = railOverride ?? this.railFor(r, segIdx);
    const station = rail?.pts.length
      ? Math.max(0, Math.min(rail.pts.length - 1, stationIdx))
      : Math.max(0, stationIdx);
    const source: RailStationSource | undefined = rail?.sources?.[station];
    return {
      segment: segIdx,
      station,
      window: source?.window ?? null,
      placementIndex: source?.placementIndex ?? null,
      pieceId: source?.pieceId ?? null,
      family: source?.family ?? null,
      zone: source?.zone ?? null,
      alt: source?.alt ?? false,
      part: source?.part ?? null,
    };
  }

  /** Count each generated path window once, before streaming can cull it.
   * This is the exposure denominator for candidate-event percentages. */
  private recordRailDebugExposure(
    data: ReturnType<EndlessWorld["get"]>,
  ): void {
    if (!this.railDebug) return;
    for (const [window, pathSeg] of data.path.segments.entries()) {
      const family = data.pieceFamilies[window] ?? "unknown";
      const pieceId = pathSeg.placementIndex >= 0
        ? data.placements[pathSeg.placementIndex]?.pieceId ?? family
        : family;
      let stat = this.railDebugPieces.get(pieceId);
      if (!stat) {
        stat = {
          pieceId,
          family,
          events: 0,
          falls: 0,
          stalls: 0,
          generatedWindows: 0,
        };
        this.railDebugPieces.set(pieceId, stat);
      }
      stat.generatedWindows++;
      this.railDebugGeneratedWindows++;
    }
  }

  /** Emit compact, rate-limited provenance. Recovery counts log at
   * 1/2/4/8… so a repeatedly bad tile becomes obvious without a 10k-racer
   * console flood. Intentional LOD handoffs log once per source tile. */
  private logRailDebug(
    reason: RailDebugReason,
    r: Racer,
    from: RailDebugTile,
    to?: RailDebugTile,
  ): void {
    if (!this.railDebug) return;
    const kind = reason === "lod-distance" ? "rail-handoff" : "recovery";
    const tileCandidate = reason === "fall" || reason === "stall";
    if (tileCandidate) {
      this.railDebugCandidateEvents++;
      const pieceId = from.pieceId ?? from.family ?? "unknown";
      let stat = this.railDebugPieces.get(pieceId);
      if (!stat) {
        stat = {
          pieceId,
          family: from.family,
          events: 0,
          falls: 0,
          stalls: 0,
          generatedWindows: 0,
        };
        this.railDebugPieces.set(pieceId, stat);
      }
      stat.events++;
      if (reason === "fall") stat.falls++;
      else stat.stalls++;
    }
    const key = [
      reason,
      from.segment,
      from.window,
      from.pieceId ?? from.family,
      from.alt ? "alt" : "main",
    ].join("|");
    const count = (this.railDebugCounts.get(key) ?? 0) + 1;
    this.railDebugCounts.set(key, count);
    const shouldLog = kind === "rail-handoff"
      ? count === 1
      : (count & (count - 1)) === 0;
    if (!shouldLog) return;
    const event: RailDebugEvent = {
      kind,
      reason,
      tileCandidate,
      count,
      seed: this.seed,
      racer: r.idx,
      s: Number(r.s.toFixed(2)),
      from,
      ...(to ? { to } : {}),
    };
    this.railDebugRecent.push(event);
    if (this.railDebugRecent.length > 12) this.railDebugRecent.shift();
    if (tileCandidate) console.warn("[rail-debug] tile candidate", event);
    else console.info("[rail-debug] rail flow", event);
  }

  /** Braid lane law (M17), run each decision: entering a braid segment
   * draws the racer's own deterministic lane (braidLaneAlt — per-racer
   * label-derived stream, hero included); and while inside one, a marble
   * that collisions have physically shoved onto the OTHER lane re-picks
   * it (the other rail's nearest station clearly closer) — the rail
   * follows the marble, never fights it. */
  private braidLaneFor(r: Racer): void {
    const segIdx = r.nearest.seg;
    if (!this.braidSegs.has(segIdx)) return;
    if (r.laneBraid !== segIdx) {
      // V3 continuation: the boundary is MID-braid — a racer whose lane
      // was drawn at the fork keeps it (no re-draw, no teleport-flip).
      const contOf = this.braidContOf.get(segIdx);
      if (contOf !== undefined && r.laneBraid === contOf) {
        r.laneBraid = segIdx;
        this.updateNearestFor(r, r.pos);
        return;
      }
      r.laneBraid = segIdx;
      // Lane identity is CHARACTER (M20a): base pilot's threshold, never
      // the rubber-banded scratch. Same label, same single draw. A
      // continuation segment keys the draw on its FORK segment (one
      // draw per BRAID, not per segment).
      const labelSeg = contOf ?? segIdx;
      let want = braidLaneAlt(this.seed, r.idx, labelSeg, r.pilot.lanePref);
      // Lane mirroring (M20b): a nearby rival bends the choice — the
      // grudge FOLLOWS its target's own label-pure draw, the ally takes
      // the opposite (hunters chase, allies spread). No stream coupling:
      // the mirrored lane is a pure function of (seed, targetIdx, seg),
      // and the proximity gate is deterministic sim state.
      const rv = r.rival;
      const target = rv ? this.racers[rv.target] : undefined;
      if (rv && target && Math.abs(target.s - r.s) < RIVAL_MIRROR_S) {
        const theirs = braidLaneAlt(
          this.seed,
          rv.target,
          labelSeg,
          target.pilot.lanePref,
        );
        want = rv.valence === "grudge" ? theirs : !theirs;
      }
      if (want !== r.laneAlt) {
        r.laneAlt = want;
        // Re-anchor on the lane rail (pre-fork stations coincide, so the
        // carried index is a valid scan anchor).
        this.updateNearestFor(r, r.pos);
      }
    }
    // Recovery: compare against the other lane near the same local s.
    const cur = this.railFor(r, segIdx);
    const other = r.laneAlt
      ? this.rails.get(segIdx)
      : this.altRails.get(segIdx);
    if (!cur || !other || other === cur) return;
    const cp = (cur.worldPts ?? cur.pts)[r.nearest.idx];
    if (!cp) return;
    const dCur = Math.hypot(
      cp[0] - r.pos[0],
      cp[1] - r.pos[1],
      cp[2] - r.pos[2],
    );
    const sLocal = cur.s[r.nearest.idx];
    let oi = 0;
    let hi = other.s.length;
    while (oi < hi) {
      const mid = (oi + hi) >> 1;
      if (other.s[mid] < sLocal) oi = mid + 1;
      else hi = mid;
    }
    const pts = other.worldPts ?? other.pts;
    let bestIdx = Math.min(oi, pts.length - 1);
    let bestD = Infinity;
    for (
      let i2 = Math.max(0, oi - 24);
      i2 <= Math.min(pts.length - 1, oi + 24);
      i2++
    ) {
      const p = pts[i2];
      const d = Math.hypot(
        p[0] - r.pos[0],
        p[1] - r.pos[1],
        p[2] - r.pos[2],
      );
      if (d < bestD) {
        bestD = d;
        bestIdx = i2;
      }
    }
    if (bestD + 0.6 < dCur) {
      r.laneAlt = !r.laneAlt;
      r.nearest = { seg: segIdx, idx: bestIdx };
    }
  }

  /** Nearest rail station to a racer, advancing across segments. */
  private updateNearestFor(
    r: Racer,
    pos: readonly [number, number, number],
  ): void {
    let { seg, idx } = r.nearest;
    if (!this.rails.has(seg)) {
      seg = Math.min(...this.rails.keys());
      idx = 0;
    }
    let bestSeg = seg;
    let bestIdx = idx;
    let bestD = Infinity;
    const consider = (sIdx: number, from: number, to: number) => {
      const rail = this.railFor(r, sIdx);
      if (!rail) return;
      // Reoriented rails compare in PRESENTED world space (worldPts is
      // the frame-applied cache); identity rails' pts ARE world.
      const pts = rail.worldPts ?? rail.pts;
      const lo = Math.max(0, from);
      const hi = Math.min(pts.length - 1, to);
      for (let i = lo; i <= hi; i++) {
        const r = pts[i];
        const d = (r[0] - pos[0]) ** 2 + (r[1] - pos[1]) ** 2 +
          (r[2] - pos[2]) ** 2;
        if (d < bestD) {
          bestD = d;
          bestSeg = sIdx;
          bestIdx = i;
        }
      }
    };
    consider(seg, idx - 25, idx + 50);
    consider(seg + 1, 0, 40); // the boundary is always in reach
    r.nearest = { seg: bestSeg, idx: bestIdx };
  }

  /** Respawn at the last checkpoint (the current segment's entry), and
   * after repeated deaths there, at the NEXT segment — forward progress is
   * guaranteed even over broken geometry. */
  private respawn(reason: "fall" | "stall"): void {
    const hero = this.racers[0];
    const debugFrom = this.railDebug && hero ? this.railDebugTile(hero) : null;
    this.deaths++;
    this.audio.cue("fall");
    this.stallClock = 0;
    this.stallHopped = false;
    this.lastProgressS = 0;
    let target = this.currentIndex;
    if (this.lastRespawnSeg === target) {
      this.respawnsHere++;
      if (this.respawnsHere >= 2) {
        target = this.currentIndex + 1;
        console.warn(
          `[endless] skipping past segment ${this.currentIndex} after ` +
            `${this.respawnsHere} failed respawns (seed ${this.seed})`,
        );
      }
    } else {
      this.respawnsHere = 0;
      this.lastRespawnSeg = target;
    }
    const cp = this.built.get(target) ??
      this.built.get(this.currentIndex) ??
      this.built.get(Math.min(...this.built.keys()));
    if (!cp || !this.engine) return;
    if (cp.index !== this.currentIndex) {
      this.currentIndex = cp.index;
      this.lastRespawnSeg = cp.index;
      this.respawnsHere = 0;
    }
    // Respawn variety (M11): if the checkpoint segment carries a diamond,
    // the retry rides the OTHER branch — the fall becomes a reveal.
    // Braid segments are exempt (M17): their lanes are PER-RACER choices
    // (braidLaneFor), and flipping the shared rail would fight them.
    const data = this.world.get(cp.index);
    if (hasAltBranch(data) && !hasBraid(data)) {
      const next = this.branchOf.get(cp.index) === "alt" ? "main" : "alt";
      this.branchOf.set(cp.index, next);
      const swapped = railForSegment(data, next === "alt");
      // The rebuilt branch rail must re-learn its presentation frames.
      if (data.frame) attachFrames(swapped, data.frame);
      this.rails.set(cp.index, swapped);
    }
    const rail = this.rails.get(cp.index);
    if (!rail) return;
    if (debugFrom && hero) {
      this.logRailDebug(
        reason,
        hero,
        debugFrom,
        this.railDebugTile(hero, cp.index, 0, rail),
      );
    }
    const t = rail.tans[0];
    const spawnPos: [number, number, number] = [
      rail.pts[0][0],
      rail.pts[0][1] + 0.4,
      rail.pts[0][2],
    ];
    const spawnVel: [number, number, number] = [t[0] * 2.2, 0, t[2] * 2.2];
    const sf = railFrameAt(rail, 0);
    if (sf) {
      affineApply(sf, spawnPos, spawnPos);
      quatRotate(sf.q, spawnVel, spawnVel);
    }
    this.engine.spawnMarble(0, spawnPos, spawnVel);
    this.nearest = { seg: cp.index, idx: 0 };
    this.marbleS = cp.startS;
    if (hero) {
      hero.nearest = { seg: cp.index, idx: 0 };
      hero.laneAlt = false;
      hero.laneBraid = -1;
      hero.s = cp.startS;
      hero.lastProgressS = cp.startS;
      hero.stallClock = 0;
      hero.stallHopped = false;
      // Same materialization as peers: scale-in + ghost-until-clear
      // (setGhost AFTER spawnMarble — spawn resets the shape to solid).
      hero.spawnAnim = SPAWN_IN_T;
      this.engine.setGhost(0, true);
      hero.ghostT = GHOST_T;
    }
  }

  /** M25: a hard knock tips nearby trackside furniture — fans, banners
   * and bumper mushrooms fall over and STAY fallen until their segment
   * sinks out. The tip axis is horizontal, perpendicular to the shove
   * (away from the hit), eased in finishFrame. */
  private tipFurnitureNear(
    seg: BuiltSegment,
    x: number,
    y: number,
    z: number,
  ): void {
    const KNOCKABLE = /^(fan-|banner|mushroom_red$)/;
    for (const group of [seg.group, seg.postGroup]) {
      if (!group) continue;
      for (const obj of group.children) {
        if (!KNOCKABLE.test(obj.name) || obj.userData.tipped) continue;
        const p = obj.getWorldPosition(this.trailPoint);
        const dx = p.x - x;
        const dz = p.z - z;
        const d2 = dx * dx + (p.y - y) * (p.y - y) + dz * dz;
        if (d2 > 0.8 * 0.8) continue;
        obj.userData.tipped = true;
        // Axis ⟂ the horizontal shove direction: the piece falls AWAY.
        const hl = Math.hypot(dx, dz) || 1;
        this.tipping.push({
          obj,
          axis: new THREE.Vector3(dz / hl, 0, -dx / hl),
          target: -(Math.PI / 2) * 0.82,
          t: 0,
        });
        break;
      }
    }
  }

  private finishFrame(dt: number): void {
    // Trail follows the CAMERA SUBJECT's ball center (the hero by
    // default; the leader when leader-cam is elected) — a trail on an
    // off-screen marble is a trail nobody sees.
    this.followedPivot.getWorldPosition(this.trailPoint);
    this.trail.update(this.trailPoint, this.ctx.camera);
    const followed = this.racers[this.followedIdx];
    if (followed?.boost.boosting) {
      if (!this.boostTrailActive) this.boostTrail.reset();
      this.boostTrailActive = true;
      this.boostTrailFade = 1;
      this.boostTrail.update(this.trailPoint, this.ctx.camera);
    } else {
      this.boostTrailActive = false;
      this.boostTrailFade = Math.max(0, this.boostTrailFade - dt * 4);
      this.boostTrail.fade(this.ctx.camera, this.boostTrailFade);
    }
    // The marble's rolling voice follows its speed.
    this.audio.setSpeed(this.speed);

    for (const built of this.built.values()) {
      for (const sp of built.spinners) {
        sp.obj.rotateOnAxis(sp.axis, sp.radPerSec * dt);
      }
      // Void decor float pass (M18b): only after the assemble wave —
      // SpawnAnim owns instance matrices while a segment is spawning
      // (writer overlap would fight the wave), and the FloatBuild's own
      // envelope ramps the bob in from the handoff.
      if (!this.spawning.has(built.index)) {
        for (const f of built.floats) f.tick(this.windT);
      }
    }

    // M25 WORLD MEMORY: drain the step's marks. Every mark parents to
    // its racer's current segment group — it sinks out with the segment
    // (ExitAnim) and its GPU state is shared (geometry/material live for
    // the session). Generation never knows: this is presentation history.
    if (this.pendingScars.length > 0) {
      this.scarGeo ??= new THREE.SphereGeometry(1, 8, 6);
      this.scarMat ??= new THREE.MeshStandardMaterial({
        color: 0x2b2b31,
        roughness: 0.95,
      });
      this.scuffMat ??= new THREE.MeshStandardMaterial({
        color: 0xbfd4e6,
        roughness: 0.9,
        transparent: true,
        opacity: 0.55,
      });
      for (const scar of this.pendingScars) {
        const r = this.racers[scar.idx];
        const segIdx = r ? r.nearest.seg : this.currentIndex;
        const seg = this.built.get(segIdx);
        if (!seg) continue;
        const count = this.scarCount.get(segIdx) ?? 0;
        if (count >= 30) continue;
        const knock = scar.speed > 0;
        const mesh = new THREE.Mesh(
          this.scarGeo,
          knock ? this.scarMat : this.scuffMat,
        );
        const s = knock ? Math.min(0.16, 0.07 + scar.speed * 0.012) : 0.06;
        mesh.scale.set(s, s * 0.35, s);
        // Segment groups may carry a presentation frame — parent in the
        // group's LOCAL space.
        seg.group.worldToLocal(
          mesh.position.set(scar.x, scar.y, scar.z),
        );
        mesh.castShadow = false;
        mesh.userData.voidDecor = true; // skip the fit box
        seg.group.add(mesh);
        this.scarCount.set(segIdx, count + 1);
        // A hard knock near trackside furniture TIPS it (fans, banners,
        // bumper mushrooms) — the wreck stays until the segment sinks.
        if (knock) this.tipFurnitureNear(seg, scar.x, scar.y, scar.z);
      }
      this.pendingScars.length = 0;
    }
    // Furniture mid-tip animations ease to their fallen pose.
    for (let i = this.tipping.length - 1; i >= 0; i--) {
      const tip = this.tipping[i];
      tip.t = Math.min(1, tip.t + dt * 2.2);
      const k = 1 - (1 - tip.t) * (1 - tip.t); // ease-out
      tip.obj.setRotationFromAxisAngle(tip.axis, tip.target * k);
      if (tip.t >= 1) this.tipping.splice(i, 1);
    }

    // Stream animations: assembly waves ahead, sink-out behind.
    for (const [idx, anim] of [...this.spawning]) {
      if (anim.update(dt)) this.spawning.delete(idx);
    }
    this.exiting = this.exiting.filter(({ group, anim, disposables }) => {
      if (!anim.update(dt)) return true;
      this.root.remove(group);
      group.clear();
      for (const d of disposables) d.dispose();
      return false;
    });

    this.toast.update(dt);

    // Minimal leaderboard: `#place/N`, hero vs. everyone, throttled —
    // hidden unless a real race is on (peers exist).
    this.placeClock += dt;
    if (this.placeClock >= PLACE_REFRESH) {
      this.placeClock = 0;
      const hero = this.racers[0];
      if (hero && this.racers.length > 1) {
        // Rival readout (M20b): flag-gated (?rival=1) while the default
        // is decided at milestone review — one quiet token, grudge +
        // rival-live only, inheriting the Kenney frame styling.
        let rivalPlace: number | undefined;
        if (this.rivalHud && hero.rival?.valence === "grudge") {
          const target = this.racers[hero.rival.target];
          if (target) {
            rivalPlace = racePlace(target.s, this.racers.map((r) => r.s));
          }
        }
        this.placeHud.set(
          racePlace(hero.s, this.racers.map((r) => r.s)),
          this.racers.length,
          rivalPlace,
        );
      } else this.placeHud.hide();
    }

    // The follow camera reads marble.s off the proxy we handed it.
    this.syncCameraProxy();

    this.statsClock += dt;
    if (this.statsClock > 0.5) {
      this.statsClock = 0;
      this.refreshStats();
    }
  }

  private cameraProxy:
    | {
      obj: THREE.Object3D;
      s: number;
      speed: number;
      radius: number;
      update: (dt: number) => void;
    }
    | null = null;

  /** Path-parameterized camera s — the followed marble's progress (only
   * differs from marbleS under leader-cam). */
  private camS = 0;

  private syncCameraProxy(): void {
    if (!this.cameraProxy) return;
    this.cameraProxy.s = this.engine ? this.camS : this.marbleS;
    this.cameraProxy.speed = this.speed;
  }

  dispose(): void {
    // Racer peers (M15): visuals + bodies go with the session.
    for (const r of this.racers) {
      if (r.idx !== 0 && r.obj) {
        disposeMarbleOwnedVisuals(r.obj);
        this.ctx.scene.remove(r.obj);
        r.obj.clear();
      }
      for (const m of r.materials) m.dispose();
      this.engine?.destroyMarble(r.idx);
    }
    disposeMarbleOwnedVisuals(this.marbleObj);
    this.engine?.dispose();
    this.engine = null;
    if (this.peerMesh) {
      this.ctx.scene.remove(this.peerMesh);
      this.peerMesh.geometry.dispose();
      (this.peerMesh.material as THREE.Material).dispose();
      this.peerMesh = null;
    }
    if (this.peerShellMesh) {
      this.ctx.scene.remove(this.peerShellMesh);
      this.peerShellMesh.geometry.dispose();
      (this.peerShellMesh.material as THREE.Material).dispose();
      this.peerShellMesh = null;
    }
    this.racers = [];
    // Session teardown is immediate — no exit waves on a seed change.
    for (const seg of [...this.built.values()]) {
      this.root.remove(seg.group);
      seg.group.clear();
      if (seg.postGroup) {
        this.root.remove(seg.postGroup);
        seg.postGroup.clear();
      }
      for (const d of seg.disposables) d.dispose();
    }
    this.built.clear();
    this.spawning.clear();
    this.fanField.clear();
    for (const { group, disposables } of this.exiting) {
      this.root.remove(group);
      group.clear();
      for (const d of disposables) d.dispose();
    }
    this.exiting = [];
    this.atmosphere.dispose();
    this.peerRing.dispose();
    this.toast.dispose();
    this.placeHud.dispose();
    this.scarGeo?.dispose();
    this.scarMat?.dispose();
    this.scuffMat?.dispose();
    this.scarGeo = null;
    this.scarMat = null;
    this.scuffMat = null;
    this.ctx.scene.remove(this.root);
    this.ctx.scene.remove(this.marbleObj);
    this.ctx.scene.remove(this.trail.obj);
    this.trail.dispose();
    this.ctx.scene.remove(this.boostTrail.obj);
    this.boostTrail.dispose();
    this.cams.retargetPath(null, null);
  }

  private refreshStats(): void {
    if (!this.ui.statsEl) return;
    const y = this.marbleObj.position.y;
    const weather = this.atmosphere.weatherKind;
    const phys = this.engine
      ? ` • physics${this.deaths > 0 ? ` (${this.deaths} falls)` : ""}`
      : "";
    this.ui.statsEl.textContent = `endless • ${this.world.style.theme} • ` +
      `${this.atmosphere.name}${weather === "none" ? "" : `+${weather}`} • ` +
      `${this.marbleS.toFixed(0)}u travelled • depth ${y.toFixed(1)} • ` +
      `${this.islandsSeen} islands • seg ${this.currentIndex}${phys} • ` +
      `${genVersion()}`;
  }
}

function countNodes(scene: THREE.Scene): number {
  let n = 0;
  scene.traverse(() => n++);
  return n;
}

function makeStarfield(): THREE.Points {
  const N = 1600;
  const positions = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    // Uniform directions, radii pushed outward so stars sit past the fog.
    const v = new THREE.Vector3(
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
    );
    if (v.lengthSq() < 1e-4) v.set(0, 1, 0);
    v.normalize().multiplyScalar(130 + Math.random() * 160);
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.y;
    positions[i * 3 + 2] = v.z;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xc7d5ff,
    size: 1.6,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    fog: false,
  });
  const points = new THREE.Points(geom, mat);
  points.name = "starfield";
  points.renderOrder = -1;
  return points;
}
